/**
 * Calendar Store (Zustand)
 *
 * State management for the Family Calendar feature.
 * No persistence — hydrated from Supabase via `load()`.
 *
 * @module features/family-calendar/stores/calendarStore
 */

import { format, parseISO } from 'date-fns';
import { bg } from 'date-fns/locale';
import { create } from 'zustand';
import { UserRole } from '@/features/auth/types/auth.types';
import { useFamilyStore } from '@/features/family/stores/familyStore';
import { toast } from '@/shared/hooks/useToast';
import { isPersistedId } from '@/shared/lib/persistedId';
import { registerFamilyScopedReset } from '@/shared/lib/storeReset';
import type { FamilyMember } from '@/shared/types/member.types';
import { StatusType } from '@/shared/types/status.types';
import { ToastVariant } from '@/shared/types/toast.types';
import { calendarService } from '../services/calendarService';
import type {
  CreateEventDTO,
  UpdateEventDTO,
} from '../services/calendarService.types';
import {
  delegatesToAll,
  rebaseUpdatesForScope,
  seriesInstants,
  withExDate,
  withoutServerPreservedKeys,
  withUntilBefore,
} from '../services/recurrenceScope';
import type {
  Anniversary,
  CreateAnniversaryDTO,
  UpdateAnniversaryDTO,
} from '../types/anniversary.types';
import type {
  Calendar,
  CalendarMember,
  EventLabel,
} from '../types/calendar.types';
import type {
  CalendarEvent,
  EventChangeRecord,
  EventChangedField,
  EventComment,
  EventUpdate,
  RecurrenceRule,
  RecurringEventUpdateScope,
  SeriesInstants,
  SeriesTarget,
  SeriesWriteResult,
  ShareLink,
  ShareLinkExpiration,
  ShareLinkPermission,
} from '../types/event.types';
import { TRACKED_EVENT_FIELDS } from '../types/event.types';
import type { Memo, CreateMemoDTO, UpdateMemoDTO } from '../types/memo.types';
import {
  getTodayDateKey,
  getRelativeDateKey,
  toLocalDateKey,
} from '../utils/date.utils';

// ============================================
// Calendar selection
// ============================================

/**
 * Pick the calendar that new rows should be written into, out of a freshly
 * hydrated list.
 *
 * Why this is not simply `calendars[0]?.id`:
 *
 * `events.calendar_id` is `uuid NOT NULL`, so the only ids Postgres will
 * accept are database uuids. The store's optimistic-write pattern means the
 * `calendars` array can legitimately hold an entity whose id is NOT a uuid —
 * `addCalendar()` inserts the caller's locally generated id immediately and
 * only swaps in the persisted row when `reconcileCalendar()` runs. Auto-
 * selecting such a calendar and then creating an event against it produces
 * Postgres `22P02: invalid input syntax for type uuid`, which offline gets
 * queued and dead-letters on replay instead of failing loudly. So the default
 * selection must skip unreconciled calendars: `isPersistedId` is exactly the
 * uuid-shape test, and the id it returns is the one the backend will accept.
 *
 * Determinism: `getCalendars()` orders by `created_at`, then `id` as a
 * tie-break, so "the first persisted calendar" is the same calendar on every
 * hydrate for every member of the family — not whichever row Postgres
 * happened to return first. Migration 026 guarantees each family has exactly
 * one calendar, so in practice this picks that one; the ordering matters for
 * families that later add more.
 *
 * Returns `null` when nothing writable exists. `null` is a real, handled
 * state — the UI blocks event creation and says so — never a sentinel string.
 */
function pickWritableCalendarId(calendars: readonly Calendar[]): string | null {
  return calendars.find((cal) => isPersistedId(cal.id))?.id ?? null;
}

// ============================================
// Write-through persistence helpers
// ============================================

// Concurrency guard for background refetches (module-level — not rendered
// state, so it never causes loading-skeleton flashes).
let refetchInFlight = false;

/**
 * Persist a store mutation to the backend. Fire-and-forget with error
 * surfacing: on failure the optional `rollback` reverts the optimistic state
 * (so no ghost data lingers that other family members can never see), the
 * failure lands in `state.error` and the user gets a destructive toast.
 */
function persistCalendarMutation(
  fallbackMessage: string,
  run: () => Promise<void>,
  rollback?: () => void
): void {
  void run().catch((error: unknown) => {
    rollback?.();
    const message = error instanceof Error ? error.message : fallbackMessage;
    useCalendarStore.setState({ error: message });
    toast({
      variant: ToastVariant.Destructive,
      title: 'Грешка при запазване',
      description: message,
    });
  });
}

// ============================================
// Edit history — a CONSEQUENCE of a write, never a precondition for one
// ============================================

/**
 * Read the edit history, or degrade to NO history. Never rejects.
 *
 * ****************************************************************************
 * ***  ИСТОРИЯ IS A SETTINGS TAB. THE CALENDAR IS THE PRODUCT. A FAILURE   ***
 * ***  OF THE FORMER MUST NEVER TAKE OUT THE LATTER.                       ***
 * ****************************************************************************
 * `getEventChanges()` sits in `load()`/`refetch()`'s `Promise.all`, and
 * `Promise.all` rejects on the FIRST rejection: an unguarded history read
 * therefore aborted the whole hydrate, and every family member got an EMPTY
 * CALENDAR — no events, no memos, no anniversaries — because one decorative
 * read failed. The feed is a list of past field diffs; nobody's day depends on
 * it, and `ActivityFeed`'s fallback branch (kept deliberately, decision 38)
 * still renders something without it.
 *
 * ***  IT ALSO REMOVES A DEPLOY-ORDERING HAZARD, WHICH IS THE HALF THAT     ***
 * ***  BITES FIRST.                                                          ***
 * `public.event_changes` arrives with migration 029. A client bundle that
 * shipped before 029 was applied — or in the window between a deploy and a
 * `notify pgrst` — asks PostgREST for a table it does not know and is refused
 * with `PGRST205`. Hard-coupled through `Promise.all`, that made the ENTIRE
 * calendar blank for everyone until the migration landed, i.e. it made the
 * client deploy and the migration a single atomic step that nothing enforces.
 * Failing soft makes the two orderable in either direction: History is empty
 * until 029 is applied, and the calendar never notices.
 *
 * The failure is reported to the console rather than to `state.error` or a
 * toast, deliberately. `state.error` is rendered as „the calendar failed to
 * load" and a toast would tell the family something is broken about the thing
 * they are looking at, which is the opposite of true. (Monitoring is NOT
 * imported here on purpose: `monitoringService` boots the Sentry client at
 * module load, and this store is imported by suites that mock neither.)
 */
async function loadEventChangeLogSoft(): Promise<Readonly<
  Record<string, readonly EventChangeRecord[]>
> | null> {
  try {
    return await calendarService.getEventChanges();
  } catch (error) {
    console.warn(
      '[calendarStore] edit history unavailable; the calendar loaded without it',
      error
    );
    // ************************************************************************
    // ***  `null` MEANS "THE READ FAILED". `{}` MEANS "THERE IS NO          ***
    // ***  HISTORY". THEY ARE NOT THE SAME ANSWER.                          ***
    // ************************************************************************
    // Both loaders REPLACE `eventChangeLog` wholesale (decision 37: a merge
    // would keep records authored against an unreconciled optimistic event,
    // which `isPersistedId`-gating means were never persisted). Returning `{}`
    // from the failure branch therefore fed a legitimate-looking empty answer
    // into that replace, and one flaky `refetch` — a dropped connection, a
    // 500, a token refresh mid-flight — silently BLANKED an История the family
    // had been reading a second earlier. Failing soft was right; failing soft
    // and destructively was not.
    //
    // The caller keeps what it already holds on `null`. That is not the merge
    // decision 37 rejected: nothing new is folded in, so no record the server
    // does not hold can appear — the previous REPLACE simply does not happen.
    return null;
  }
}

/** Put one event's local history back the way it was before an optimistic add. */
function restoreChangeLog(
  eventId: string,
  previousLog: readonly EventChangeRecord[] | undefined
): void {
  useCalendarStore.setState((state) => {
    const { [eventId]: _dropped, ...restLog } = state.eventChangeLog;
    return {
      eventChangeLog: previousLog
        ? { ...restLog, [eventId]: previousLog }
        : restLog,
    };
  });
}

/** Title and body of the one thing the user IS told when history is refused. */
const CHANGE_LOG_FAILED_TITLE = 'Промяната е запазена';
const CHANGE_LOG_FAILED_MESSAGE =
  'Промяната беше запазена, но не влезе в История. Останалото е непроменено.';

/**
 * Persist one edit's history batch WITHOUT the power to undo that edit.
 *
 * ****************************************************************************
 * ***  A HISTORY ROW IS A CONSEQUENCE OF THE WRITE, NOT A PRECONDITION.    ***
 * ****************************************************************************
 * This used to be awaited bare inside `persistCalendarMutation`'s closure, so a
 * PERMANENT failure of the history insert (an RLS refusal, a constraint
 * violation, a 400) rejected the closure and fired the ROLLBACK — reverting an
 * edit the server had ALREADY COMMITTED. On the series paths that is not a
 * cosmetic lie: `restoreEventsById` re-appends every child row the RPC deleted
 * and folds the pre-write master back over the returned one, so the screen
 * shows a series the database no longer has, and it stays that way until the
 * next `load()`.
 *
 * And the message was wrong twice over: `createEventChanges` threw
 * `new Error(error.message)` — raw English PostgREST prose — which
 * `persistCalendarMutation` renders VERBATIM into a destructive toast in a
 * Bulgarian-only app, announcing that a write which succeeded had failed. The
 * service now throws Bulgarian; this function does not render the thrown text
 * at all, so nothing that reaches here by another route (a rejected
 * `getCurrentFamilyId`, say) can leak English either.
 *
 * What the failure DOES do: drop the optimistic records, so История never shows
 * history the database does not hold, and say so once, non-destructively.
 */
async function persistChangeRecordsSoft(
  eventId: string,
  records: readonly EventChangeRecord[],
  previousLog: readonly EventChangeRecord[] | undefined
): Promise<void> {
  if (records.length === 0) return;
  try {
    await calendarService.createEventChanges(eventId, records);
  } catch (error) {
    restoreChangeLog(eventId, previousLog);
    console.warn(
      '[calendarStore] the edit landed but its history did not',
      error
    );
    toast({
      variant: ToastVariant.Warning,
      title: CHANGE_LOG_FAILED_TITLE,
      description: CHANGE_LOG_FAILED_MESSAGE,
    });
  }
}

/** Map a fully-built optimistic event to the service create DTO. */
function eventToCreateDTO(event: CalendarEvent): CreateEventDTO {
  return {
    calendarId: event.calendarId,
    title: event.title,
    startDate: event.startDate,
    isAllDay: event.isAllDay,
    participants: event.participants,
    reminders: event.reminders,
    isPinned: event.isPinned,
    attachments: event.attachments,
    ...(event.description !== undefined && { description: event.description }),
    ...(event.emoji !== undefined && { emoji: event.emoji }),
    ...(event.endDate !== undefined && { endDate: event.endDate }),
    ...(event.location !== undefined && { location: event.location }),
    ...(event.url !== undefined && { url: event.url }),
    ...(event.labelId !== undefined && { labelId: event.labelId }),
    ...(event.recurrence !== undefined && { recurrence: event.recurrence }),
  };
}

/** Map a store-level event patch to the service update DTO (defined keys only). */
function eventUpdatesToDTO(updates: EventUpdate): UpdateEventDTO {
  return {
    ...(updates.title !== undefined && { title: updates.title }),
    ...(updates.description !== undefined && {
      description: updates.description,
    }),
    ...(updates.emoji !== undefined && { emoji: updates.emoji }),
    ...(updates.startDate !== undefined && { startDate: updates.startDate }),
    ...(updates.endDate !== undefined && { endDate: updates.endDate }),
    ...(updates.isAllDay !== undefined && { isAllDay: updates.isAllDay }),
    ...(updates.location !== undefined && { location: updates.location }),
    ...(updates.url !== undefined && { url: updates.url }),
    ...(updates.labelId !== undefined && { labelId: updates.labelId }),
    ...(updates.participants !== undefined && {
      participants: updates.participants,
    }),
    // ************************************************************************
    // ***  KEY PRESENCE, NOT `!== undefined`. THIS ONE IS NOT A STYLE NIT.  ***
    // ************************************************************************
    // `EventUpdate` gives an optional field THREE states and the modal uses all
    // three: absent = "not edited", a value = "set it", explicit `undefined` =
    // „Без повторение", i.e. CLEAR it. A `!== undefined` test collapses the
    // first and third, so choosing „Без повторение" cleared the rule locally,
    // emitted a "removed" diff and showed a success toast while the PATCH
    // carried no `recurrence` key at all — the server kept the rule and the
    // next expander run went on materialising occurrences of a series the user
    // had been told was no longer repeating.
    //
    // The DTO's `recurrence` was widened to `| null` in the same wave so this
    // mapping can be typed; the service's own `data.recurrence !== undefined`
    // guard then writes `recurrence: null` to the column unchanged.
    //
    // The other optional fields still use `!== undefined` and therefore still
    // cannot be cleared through this mapper. That is a pre-existing, separately
    // filed gap (`privateNotes` has never persisted at all) — deliberately not
    // widened here, because each of those DTO fields would need its own `| null`
    // and its own column semantics.
    ...('recurrence' in updates && {
      recurrence: updates.recurrence ?? null,
    }),
    ...(updates.reminders !== undefined && { reminders: updates.reminders }),
    ...(updates.isPinned !== undefined && { isPinned: updates.isPinned }),
    ...(updates.attachments !== undefined && {
      attachments: updates.attachments,
    }),
  };
}

/** Swap an optimistic event for its persisted counterpart. */
function reconcileEvent(optimisticId: string, persisted: CalendarEvent): void {
  useCalendarStore.setState((state) => {
    const { [optimisticId]: optimisticLog, ...restLog } = state.eventChangeLog;
    return {
      events: state.events
        .filter((e) => e.id !== persisted.id || e.id === optimisticId)
        .map((e) => (e.id === optimisticId ? persisted : e)),
      selectedEventId:
        state.selectedEventId === optimisticId
          ? persisted.id
          : state.selectedEventId,
      eventChangeLog: optimisticLog
        ? { ...restLog, [persisted.id]: optimisticLog }
        : state.eventChangeLog,
    };
  });
}

/** Swap an optimistic calendar for its persisted counterpart. */
function reconcileCalendar(optimisticId: string, persisted: Calendar): void {
  useCalendarStore.setState((state) => ({
    calendars: state.calendars
      .filter((c) => c.id !== persisted.id || c.id === optimisticId)
      .map((c) =>
        c.id === optimisticId
          ? { ...persisted, labels: c.labels, shareLinks: c.shareLinks }
          : c
      ),
    events: state.events.map((e) =>
      e.calendarId === optimisticId ? { ...e, calendarId: persisted.id } : e
    ),
    labels: state.labels.map((l) =>
      l.calendarId === optimisticId ? { ...l, calendarId: persisted.id } : l
    ),
    calendarMembers: state.calendarMembers.map((cm) =>
      cm.calendarId === optimisticId ? { ...cm, calendarId: persisted.id } : cm
    ),
    memos: state.memos.map((m) =>
      m.calendarId === optimisticId ? { ...m, calendarId: persisted.id } : m
    ),
    anniversaries: state.anniversaries.map((a) =>
      a.calendarId === optimisticId ? { ...a, calendarId: persisted.id } : a
    ),
    selectedCalendarId:
      state.selectedCalendarId === optimisticId
        ? persisted.id
        : state.selectedCalendarId,
    visibleCalendarIds: state.visibleCalendarIds.map((id) =>
      id === optimisticId ? persisted.id : id
    ),
  }));
}

/** Swap an optimistic label for its persisted counterpart. */
function reconcileLabel(optimisticId: string, persisted: EventLabel): void {
  useCalendarStore.setState((state) => ({
    labels: state.labels
      .filter((l) => l.id !== persisted.id || l.id === optimisticId)
      .map((l) => (l.id === optimisticId ? persisted : l)),
    calendars: state.calendars.map((c) =>
      c.id === persisted.calendarId
        ? {
            ...c,
            labels: c.labels.map((l) =>
              l.id === optimisticId ? persisted : l
            ),
          }
        : c
    ),
    events: state.events.map((e) =>
      e.labelId === optimisticId ? { ...e, labelId: persisted.id } : e
    ),
  }));
}

/** Swap an optimistic comment for its persisted counterpart (inside events). */
function reconcileComment(optimisticId: string, persisted: EventComment): void {
  useCalendarStore.setState((state) => ({
    events: state.events.map((e) =>
      e.id === persisted.eventId
        ? {
            ...e,
            comments: e.comments
              .filter((c) => c.id !== persisted.id || c.id === optimisticId)
              .map((c) => (c.id === optimisticId ? persisted : c)),
          }
        : e
    ),
  }));
}

/** Swap an optimistic memo for its persisted counterpart. */
function reconcileMemo(optimisticId: string, persisted: Memo): void {
  useCalendarStore.setState((state) => ({
    memos: state.memos
      .filter((m) => m.id !== persisted.id || m.id === optimisticId)
      .map((m) => (m.id === optimisticId ? persisted : m)),
  }));
}

/** Swap an optimistic anniversary for its persisted counterpart. */
function reconcileAnniversary(
  optimisticId: string,
  persisted: Anniversary
): void {
  useCalendarStore.setState((state) => ({
    anniversaries: state.anniversaries
      .filter((a) => a.id !== persisted.id || a.id === optimisticId)
      .map((a) => (a.id === optimisticId ? persisted : a)),
  }));
}

// ============================================
// State Interface
// ============================================

/**
 * Calendar store state interface
 */
interface CalendarState {
  // Data state
  readonly calendars: readonly Calendar[];
  readonly events: readonly CalendarEvent[];
  readonly labels: readonly EventLabel[];
  readonly members: readonly FamilyMember[];
  readonly calendarMembers: readonly CalendarMember[];
  readonly memos: readonly Memo[];
  readonly anniversaries: readonly Anniversary[];

  // Field-level change history per event (forward-only, keyed by event id)
  readonly eventChangeLog: Record<string, readonly EventChangeRecord[]>;

  // UI state
  readonly selectedCalendarId: string | null;
  readonly selectedEventId: string | null;
  readonly selectedDate: string | null; // ISO date string (YYYY-MM-DD) - null when no day is selected
  readonly viewedDate: string; // ISO date string (YYYY-MM-DD) - which month is being displayed
  readonly viewMode: CalendarViewMode;
  readonly isLoading: boolean;
  /** True once a load attempt has settled (success or failure) for the active family. */
  readonly isLoaded: boolean;
  readonly error: string | null;

  // Calendar visibility (for multi-calendar view)
  readonly visibleCalendarIds: readonly string[]; // Which calendars are visible
  readonly showAllCalendars: boolean; // Whether to show combined view

  // Calendar CRUD
  setCalendars: (calendars: readonly Calendar[]) => void;
  addCalendar: (calendar: Calendar) => void;
  updateCalendar: (id: string, updates: Partial<Calendar>) => void;
  deleteCalendar: (id: string) => void;
  selectCalendar: (id: string | null) => void;

  // Event CRUD
  setEvents: (events: readonly CalendarEvent[]) => void;
  addEvent: (event: CalendarEvent) => void;
  updateEvent: (id: string, updates: EventUpdate, changedBy?: string) => void;
  deleteEvent: (id: string) => void;
  /**
   * Edit ONE occurrence of a series, an occurrence and everything after it, or
   * the whole series — atomically, through `update_event_series`.
   *
   * A SEPARATE ACTION RATHER THAN AN EXTRA PARAMETER ON `updateEvent`.
   * `calendarStore.realtimeDelete.test.ts` asserts
   * `toHaveBeenCalledWith(EVENT_ID)` exactly, so widening the plain actions'
   * arity would break a suite that has nothing to do with series; and the two
   * paths have genuinely different failure and rollback shapes.
   */
  updateEventSeries: (
    target: SeriesTarget,
    scope: RecurringEventUpdateScope,
    updates: EventUpdate,
    changedBy?: string
  ) => void;
  /** Delete at the same three scopes, through `delete_event_series`. */
  deleteEventSeries: (
    target: SeriesTarget,
    scope: RecurringEventUpdateScope
  ) => void;
  /**
   * Merge an event received from a realtime stream (P7). Inserts when unseen,
   * replaces when the incoming row is strictly newer (dedup by id +
   * updatedAt). Never recomputes the change log — realtime is external truth.
   */
  upsertRealtimeEvent: (event: CalendarEvent) => void;
  /**
   * Remove an event deleted on another device (P7). Local-only — does NOT call
   * the service, so a DELETE echo never re-issues to the server the delete
   * that produced it. Use {@link CalendarState.deleteEvent} for user-initiated
   * deletions.
   */
  deleteRealtimeEvent: (id: string) => void;
  toggleEventPin: (id: string) => void;
  selectEvent: (id: string | null) => void;

  // Label CRUD
  addLabel: (label: EventLabel) => void;
  updateLabel: (id: string, updates: Partial<EventLabel>) => void;
  deleteLabel: (id: string) => void;

  // Calendar Members
  addCalendarMember: (calendarMember: CalendarMember) => void;
  updateCalendarMember: (
    calendarId: string,
    memberId: string,
    updates: Partial<
      Pick<CalendarMember, 'displayNameOverride' | 'avatarOverride' | 'role'>
    >
  ) => void;
  removeCalendarMember: (calendarId: string, memberId: string) => void;

  // Event Comments
  addComment: (eventId: string, text: string, authorId: string) => EventComment;
  updateComment: (eventId: string, commentId: string, text: string) => void;
  deleteComment: (eventId: string, commentId: string) => void;

  // Memos
  addMemo: (data: CreateMemoDTO) => Memo;
  updateMemo: (id: string, updates: UpdateMemoDTO) => void;
  deleteMemo: (id: string) => void;
  toggleMemoPin: (id: string) => void;

  // Anniversaries
  addAnniversary: (data: CreateAnniversaryDTO) => Anniversary;
  updateAnniversary: (id: string, updates: UpdateAnniversaryDTO) => void;
  deleteAnniversary: (id: string) => void;

  // Share Links
  generateShareLink: (
    calendarId: string,
    permission: ShareLinkPermission,
    expiration: ShareLinkExpiration,
    createdById: string,
    createdByName?: string
  ) => Promise<ShareLink>;
  revokeShareLink: (calendarId: string, linkId: string) => void;
  getShareLinksForCalendar: (calendarId: string) => readonly ShareLink[];

  // UI Actions
  setSelectedDate: (date: string | null) => void;
  setViewedDate: (date: string) => void;
  setViewMode: (mode: CalendarViewMode) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Calendar Visibility Actions
  toggleCalendarVisibility: (calendarId: string) => void;
  setShowAllCalendars: (show: boolean) => void;
  setVisibleCalendars: (calendarIds: readonly string[]) => void;
  showOnlyCalendar: (calendarId: string) => void;
  showAllCalendarsView: () => void;
  getVisibleEvents: () => readonly CalendarEvent[];

  // Data initialization
  /**
   * Hydrate the store from Supabase: calendars, events, memos, anniversaries,
   * labels and members. Idempotent — runs at most once per active family
   * (guarded by `isLoaded` / `isLoading`), including when the family legitimately
   * has no calendars yet or the read failed.
   */
  load: () => Promise<void>;
  /**
   * Force a fresh read from the backend, bypassing the `isLoaded` /
   * `isLoading` guards, and replace the store data with the result while
   * preserving the user's calendar selection and visibility choices. Used for
   * catch-up after a realtime reconnect and on window focus/online events.
   * Never toggles `isLoading` (no skeleton flashes).
   */
  refetch: () => Promise<void>;
  reset: () => void;
}

// ============================================
// Types
// ============================================

/**
 * Calendar view modes
 */
export type CalendarViewMode = 'month' | 'week' | 'day' | 'agenda';

// ============================================
// Initial State
// ============================================

const getInitialDate = (): string => {
  return getTodayDateKey();
};

const initialState = {
  calendars: [] as readonly Calendar[],
  events: [] as readonly CalendarEvent[],
  labels: [] as readonly EventLabel[],
  members: [] as readonly FamilyMember[],
  calendarMembers: [] as readonly CalendarMember[],
  memos: [] as readonly Memo[],
  anniversaries: [] as readonly Anniversary[],
  eventChangeLog: {} as Record<string, readonly EventChangeRecord[]>,
  selectedCalendarId: null,
  selectedEventId: null,
  selectedDate: getInitialDate(), // Auto-select today's date
  viewedDate: getInitialDate(), // Which month to display
  viewMode: 'month' as CalendarViewMode,
  isLoading: false,
  isLoaded: false,
  error: null,
  // Calendar visibility for multi-calendar view
  visibleCalendarIds: [] as readonly string[], // Populated on load()
  showAllCalendars: true, // Start with all calendars visible
};

// ============================================
// Change History Helpers
// ============================================

/**
 * Format a tracked event field into a human-readable display string for the
 * change history. Returns `null` when the field is unset/empty.
 */
const formatFieldValue = (
  event: CalendarEvent,
  field: EventChangedField
): string | null => {
  switch (field) {
    case 'title':
      return event.title.trim() || null;
    case 'emoji':
      return event.emoji?.trim() || null;
    case 'description':
      return event.description?.trim() || null;
    case 'startDate':
    case 'endDate': {
      const value = event[field];
      if (!value) return null;
      const date = parseISO(value);
      return event.isAllDay
        ? format(date, 'd MMM yyyy', { locale: bg })
        : format(date, 'd MMM yyyy, HH:mm', { locale: bg });
    }
    case 'isAllDay':
      return event.isAllDay ? 'Цял ден' : 'С часове';
    case 'location':
      return event.location?.trim() || null;
    case 'url':
      return event.url?.trim() || null;
    case 'labelId':
      return event.labelId || null;
    case 'recurrence': {
      // FREQUENCY ALONE IS NOT ENOUGH, and the gap is exactly the question
      // История exists to answer. A scoped delete writes an exDate; a
      // „това и следващите" delete writes an `until`. Neither touches
      // `frequency`, so with only the frequency in the diff both produce NO
      // record at all and „кой отмени вторник?" is unanswerable — which is the
      // wrong answer to arrive at silently, on a table with no UPDATE and no
      // DELETE policy.
      const rule = event.recurrence;
      if (!rule) return null;
      const parts: string[] = [rule.frequency];
      if (rule.until) {
        parts.push(
          `до ${format(parseISO(rule.until), 'd MMM yyyy', { locale: bg })}`
        );
      }
      const exCount = rule.exDates?.length ?? 0;
      if (exCount > 0) {
        parts.push(`${exCount} ${exCount === 1 ? 'изключение' : 'изключения'}`);
      }
      return parts.join(' · ');
    }
    case 'reminders':
      return event.reminders.length > 0 ? String(event.reminders.length) : null;
    case 'isPinned':
      return event.isPinned ? 'Да' : 'Не';
    default:
      return null;
  }
};

// ============================================
// Series write helpers
// ============================================

/**
 * True for a row that belongs to a recurring series — the master itself, a
 * generated child, or an authored exception.
 *
 * The two tests are not redundant. A MASTER carries the rule and no parent; a
 * generated child carries `recurrence: null` and a parent, and is therefore
 * indistinguishable from a one-off event by `recurrence` alone. Both must be
 * refused by the plain single-row paths.
 */
function isSeriesRow(event: CalendarEvent): boolean {
  return event.recurrence != null || event.parentEventId != null;
}

/** Message both defensive refusals surface. */
const SERIES_PLAIN_PATH_MESSAGE =
  'Повтарящо се събитие се променя само през избора на обхват.';

/**
 * Refuse a single-row write against a series row, loudly.
 *
 * WHY A GUARD AND NOT A COMMENT. The single-row paths write through the offline
 * queue, whose entry is `{table, op, matchId}` replayed as `.eq('id', matchId)`
 * — no exDate, no rule change, nothing the expander can see. A queued plain
 * DELETE of a materialised child therefore replays successfully and the next
 * cron run re-creates the row at the same instant, because the upsert keys on
 * `(parent_event_id, start_date)` and the rule still generates it. Series
 * writes are refused offline entirely, so the new code can never mint such an
 * entry again; this guard is what makes "can never" structural rather than a
 * property of every call site staying correct.
 */
function refuseSeriesOnPlainPath(): void {
  useCalendarStore.setState({ error: SERIES_PLAIN_PATH_MESSAGE });
  toast({
    variant: ToastVariant.Destructive,
    title: 'Грешка при запазване',
    description: SERIES_PLAIN_PATH_MESSAGE,
  });
}

/** What the user is told when the master a scoped write names is not in state. */
const SERIES_MASTER_MISSING_MESSAGE = 'Поредицата не беше намерена.';

/**
 * Refuse a scoped series write whose master is gone, LOUDLY.
 *
 * WHY THIS IS NOT A BARE `set({ error })`. Both modals fire their success toast
 * and close SYNCHRONOUSLY, immediately after calling the action — they have to,
 * because `persistCalendarMutation` is fire-and-forget and there is nothing to
 * await. So a quiet bail here is the one shape that produces the worst possible
 * outcome: „Събитието е изтрито", the sheet closes, the occurrence is still on
 * screen, and `state.error` is a field nothing renders. This raises the same
 * destructive toast the async failure path raises, so the two agree.
 *
 * Reachable when another device deleted the series between the render that
 * resolved the target and the confirm — realtime removes the master from
 * `state.events` while the modal is open.
 */
function refuseSeriesWrite(): void {
  useCalendarStore.setState({ error: SERIES_MASTER_MISSING_MESSAGE });
  toast({
    variant: ToastVariant.Destructive,
    title: 'Грешка при запазване',
    description: SERIES_MASTER_MISSING_MESSAGE,
  });
}

/**
 * Forward-only field diff between two versions of one event.
 *
 * REFUSES TO RECORD WHEN THE EDITOR IS UNKNOWN, rather than falling back to
 * `prev.createdBy`. That fallback attributed every edit to the event's CREATOR
 * — the exact wrong answer `event_changes` exists to end, arriving through a
 * different door and now written PERMANENTLY to a table with no UPDATE and no
 * DELETE policy. `currentUser?.id` is `string | undefined`, so a stale or
 * unauthenticated auth store reaches here with nothing; and the row would be
 * refused by RLS anyway (`changed_by = auth.uid()` cannot be satisfied with
 * somebody else's id), so the fallback only ever produced a local record that
 * could never persist.
 */
function diffEventFields(
  prev: CalendarEvent,
  next: CalendarEvent,
  changedBy: string | undefined,
  changedAt: string
): EventChangeRecord[] {
  if (changedBy === undefined) {
    console.warn(
      '[calendarStore] change records skipped: no editor id was supplied'
    );
    return [];
  }
  const records: EventChangeRecord[] = [];
  for (const field of TRACKED_EVENT_FIELDS) {
    const oldValue = formatFieldValue(prev, field);
    const newValue = formatFieldValue(next, field);
    if (oldValue !== newValue) {
      records.push({ field, oldValue, newValue, changedBy, changedAt });
    }
  }
  return records;
}

/**
 * Restore a set of rows BY ID after a failed series write.
 *
 * ***  NEVER `events: previousEvents`.  ***
 * A wholesale restore also un-does everything that arrived between the
 * optimistic set and the failure — realtime inserts from other devices land in
 * that window and would silently vanish. Restoring by id touches exactly the
 * rows this write touched: changed rows are replaced, deleted rows are
 * re-appended, and anything else in `state.events` is left alone. Today's
 * single-row `deleteEvent` rollback re-appends exactly one event and is not a
 * template for a leg that can move a master, a tail and ninety children.
 */
function restoreEventsById(snapshot: readonly CalendarEvent[]): void {
  if (snapshot.length === 0) return;
  useCalendarStore.setState((state) => {
    const byId = new Map(snapshot.map((e) => [e.id, e] as const));
    const restored = state.events.map((e) => byId.get(e.id) ?? e);
    const present = new Set(restored.map((e) => e.id));
    const missing = snapshot.filter((e) => !present.has(e.id));
    return {
      events: missing.length > 0 ? [...restored, ...missing] : restored,
    };
  });
}

/** Every row a series leg could touch: the master and everything under it. */
function seriesRowsOf(
  events: readonly CalendarEvent[],
  masterId: string
): readonly CalendarEvent[] {
  return events.filter(
    (e) => e.id === masterId || e.parentEventId === masterId
  );
}

/**
 * A patch stripped of everything that describes the ROW rather than the event.
 *
 * ***  NEVER SPREAD A RAW SERIES PATCH OVER A ROW THE USER DID NOT AUTHOR.  ***
 * `EditEventModal` builds the patch from the MASTER — the click path routes
 * every series pill there — so it carries `id`, `createdAt`, `createdBy`,
 * `comments` and `attachments` along with the title. Spread unfiltered onto the
 * edited occurrence, `id` gives TWO rows in `state.events` the same id (every
 * `find`/`map` keyed on id then silently resolves to whichever comes first),
 * and `comments`/`attachments` replace that row's own thread and files with the
 * master's.
 */
function withoutRowIdentity(updates: EventUpdate): EventUpdate {
  const {
    id: _i,
    parentEventId: _p,
    isOccurrence: _o,
    createdAt: _c,
    createdBy: _b,
    comments: _cm,
    attachments: _at,
    // ***  NOT ROW IDENTITY — COLUMNS THE WIRE DOES NOT CARRY.  ***
    // Neither `eventUpdatesToDTO` nor `eventColumnPatch` ever emits
    // `calendar_id` or `private_notes`, so 029's patch arms for those two keys
    // are DEAD: nothing this client can build ever puts them in `p_patch`.
    // `EditEventModal` nevertheless stamps both onto every patch it makes
    // (`calendarId: displayEvent.calendarId`, `privateNotes: data.notes...`),
    // so spreading them here showed the family a change the server was never
    // told about — one that then survived on screen until the next `load()`
    // quietly replaced it with the stored value.
    //
    // The honest optimistic picture is the one the server will agree with, so
    // both keys are dropped rather than displayed. `privateNotes` never
    // persisting AT ALL is the separately filed pre-existing gap; this only
    // stops the series paths from pretending otherwise.
    calendarId: _cal,
    privateNotes: _pn,
    ...rest
  } = updates;
  return rest;
}

/**
 * The content half of a patch — {@link withoutRowIdentity} minus the dates and
 * the rule.
 *
 * What survives is deliberately the key set `update_event_series` E-d step 2b
 * can actually write server-side (title, description, emoji, is_all_day,
 * location, url, label_id, participants, reminders, is_pinned), so the
 * optimistic picture of a content-only `all` edit and the persisted one agree
 * column for column. `calendar_id` and `private_notes` are NOT in that set even
 * though the SQL has arms for them — no client mapper emits either key, so
 * those arms are unreachable and {@link withoutRowIdentity} drops them above.
 * The dates and the rule are excluded because only the
 * prune and the insert may move a generated child in time, and a child carries
 * no rule at all.
 */
function contentOnly(updates: EventUpdate): EventUpdate {
  const {
    startDate: _s,
    endDate: _e,
    recurrence: _r,
    ...rest
  } = withoutRowIdentity(updates);
  return rest;
}

/**
 * A value stringified so that EQUAL VALUES PRODUCE EQUAL STRINGS — object keys
 * sorted, `undefined`-valued keys dropped.
 *
 * `JSON.stringify` is key-ORDER sensitive and `jsonb` equality is not, so a
 * rule spelled `{interval, frequency}` and the same rule spelled
 * `{frequency, interval}` compare unequal here and equal in Postgres. Dropping
 * `undefined` mirrors the wire: an optional key carrying `undefined` never
 * survives `JSON.stringify` on the way to `p_patch`, so the SQL's
 * `?`-presence tests never see it either.
 */
function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  return `{${entries
    .map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`)
    .join(',')}}`;
}

/**
 * The rule an `all` edit will actually LEAVE ON THE MASTER — the client half of
 * `update_event_series` E-d step 1's merge.
 *
 * The SQL preserves `exDates` and `until` from the stored row unless the patch
 * sets them itself (decision 18, the second line of defence behind
 * `EditEventModal`'s rule merge), so the rule that goes IN is not the rule that
 * comes OUT, and the server's own rebuild test compares the merged result.
 * Anything on this side that compares the raw patch instead answers a different
 * question. See {@link touchesSchedule}.
 */
function mergedSeriesRule(
  master: CalendarEvent,
  updates: EventUpdate
): RecurrenceRule | null {
  // `v_has_rule := v_patch ? 'recurrence'`. `eventUpdatesToDTO` emits the key
  // for any PRESENT key, mapping an explicit `undefined` to a JSON null, which
  // is `v_clears_rule`.
  if (!('recurrence' in updates)) return master.recurrence ?? null;
  const patchRule = updates.recurrence;
  if (patchRule == null) return null;
  const existing = master.recurrence;
  if (existing == null) return patchRule;
  return {
    ...patchRule,
    // `e.recurrence ? 'exDates' and not v_patch_rule ? 'exDates'`. Tested with
    // `!== undefined` rather than `in`, because a key carrying `undefined` is
    // dropped by `JSON.stringify` and is therefore ABSENT in the jsonb.
    ...(existing.exDates !== undefined &&
      patchRule.exDates === undefined && { exDates: existing.exDates }),
    ...(existing.until !== undefined &&
      patchRule.until === undefined && { until: existing.until }),
  };
}

/**
 * Does this patch move the series in time, or change what it generates?
 *
 * ***  THIS IS `v_rebuild` FROM 029 E-d STEP 2, AND IT MUST STAY THAT.  ***
 * The distinction decides whether an `all` edit REBUILDS the generated children
 * or corrects them in place — and BOTH SIDES DECIDE IT SEPARATELY, the server
 * for the rows and this function for the optimistic picture. Where they
 * disagree in this direction the damage is silent: the client deletes every
 * generated child locally, the server keeps them, `applySeriesResult` returns
 * only the master, and the series stays half-empty on screen until the next
 * `load()` — with the calendar looking plausible throughout, because Major 1
 * redraws the missing rows virtually.
 *
 * It used to disagree in exactly that direction, twice over:
 *   * it compared the PATCH's rule with the stored one, while the SQL compares
 *     the MERGED rule — so a patch that omitted `exDates` (anything not built
 *     by `EditEventModal`'s merge) read as a rule change here and as no change
 *     at all there;
 *   * `JSON.stringify` made a key REORDERING a rule change, which `jsonb`
 *     equality never does.
 * Both are now derived from the SQL rather than resembling it, and
 * `calendarStore.rebuildParity.test.ts` fails if either side moves alone.
 *
 * The answer must be VALUE INEQUALITY rather than key presence on both sides:
 * `EditEventModal` sends the full merged rule on every edit of a recurring
 * event, so a key-presence test would rebuild the whole series on a bare title
 * change — deleting every child row for however long the cron takes to come
 * back, and with it a week of `notify-event-reminder`'s window and everything
 * `get_shared_calendar` reports.
 */
function touchesSchedule(master: CalendarEvent, updates: EventUpdate): boolean {
  // `v_content_patch ? 'start_date' and coalesce(v_patched.start_date, …) is
  // distinct from v_master.start_date`. `!= null` IS the presence test:
  // `eventColumnPatch` emits `start_date` only for a defined value.
  const movedStart =
    updates.startDate != null &&
    Date.parse(updates.startDate) !== Date.parse(master.startDate);
  // `v_content_patch ? 'end_date' and v_patched.end_date is distinct from
  // v_master.end_date`. NOT `'endDate' in updates`: a key carrying an explicit
  // `undefined` is dropped by `eventColumnPatch`, so it never reaches the
  // jsonb and the SQL's presence test is false for it — while the old test
  // read it as a CLEAR and rebuilt the series the server had left standing.
  const patchedEnd = updates.endDate;
  const movedEnd =
    patchedEnd !== undefined &&
    (master.endDate == null ||
      Date.parse(patchedEnd) !== Date.parse(master.endDate));
  // `v_new_rule is distinct from v_old_rule`, where `v_new_rule` is re-read
  // from the row AFTER the merge.
  const ruleChanged =
    canonicalJson(mergedSeriesRule(master, updates)) !==
    canonicalJson(master.recurrence ?? null);
  return movedStart || movedEnd || ruleChanged;
}

interface SeriesLegContext {
  readonly master: CalendarEvent;
  readonly target: SeriesTarget;
  readonly scope: RecurringEventUpdateScope;
  readonly instants: SeriesInstants;
  /**
   * The exDate this leg writes, as `seriesExDate` decided it — `undefined`
   * for a leg that writes none, `null` for „none owed", an ISO instant
   * otherwise.
   *
   * IT IS THE SAME VALUE `instants.exDate` CARRIES TO THE SERVER, and that is
   * the point: appending anything else here means `applySeriesResult` folds the
   * returned master back over a local rule that disagrees with it, and the
   * cancelled occurrence reappears the instant the RPC answers.
   */
  readonly exDate: string | null | undefined;
}

/**
 * The optimistic picture after a scoped DELETE, before the RPC answers.
 *
 * The exDates append is the part that matters on screen: Major 1 projects the
 * series from the master's LOCAL rule, so appending the cancelled instant is
 * what makes the projected pill vanish IMMEDIATELY rather than at the next
 * refetch. Dropping a materialised row alone would leave the projection behind
 * it visible, and the occurrence would appear to come straight back.
 */
function optimisticSeriesDelete(
  events: readonly CalendarEvent[],
  ctx: SeriesLegContext
): readonly CalendarEvent[] {
  const { master, target, scope, instants, exDate } = ctx;
  const targetMs = Date.parse(target.occurrenceStart);
  const isChildOf = (e: CalendarEvent): boolean =>
    e.parentEventId === master.id;

  if (scope === 'all') {
    // The master and every row under it. Server-side this is ONE statement —
    // the FK cascade takes the children, including authored exceptions another
    // member owns, because referential actions are not RLS-checked.
    return events.filter((e) => e.id !== master.id && !isChildOf(e));
  }

  if (scope === 'this_and_following') {
    const kept = events.filter(
      (e) => !(isChildOf(e) && Date.parse(e.startDate) >= targetMs)
    );
    return kept.map((e) =>
      e.id === master.id && e.recurrence
        ? {
            ...e,
            recurrence: withUntilBefore(e.recurrence, new Date(targetMs)),
          }
        : e
    );
  }

  // scope `this`
  if (target.isMasterOccurrence) {
    const nextStart = instants.nextStart;
    if (nextStart === undefined) {
      // No further occurrence to roll onto: the series is the master alone.
      return events.filter((e) => e.id !== master.id && !isChildOf(e));
    }
    const nextMs = Date.parse(nextStart);
    return (
      events
        // The instant the master is about to occupy must be freed first — a
        // master and a child on the same day is NOT caught by
        // events_parent_occurrence_uq, because masters carry
        // `parent_event_id = null` and Postgres treats NULLs as distinct.
        .filter((e) => !(isChildOf(e) && Date.parse(e.startDate) === nextMs))
        .map((e) =>
          e.id === master.id
            ? {
                ...e,
                startDate: nextStart,
                ...(instants.nextEnd !== undefined && {
                  endDate: instants.nextEnd,
                }),
                ...(e.recurrence?.count !== undefined && {
                  // Decrementing is not optional: the engine recomputes
                  // `remaining = count - 1` from the NEW start, so a count-bounded
                  // series would silently GAIN an occurrence on every roll.
                  recurrence: {
                    ...e.recurrence,
                    count: Math.max(1, e.recurrence.count - 1),
                  },
                }),
              }
            : e
        )
    );
  }

  const dropped = events.filter(
    (e) => !(isChildOf(e) && Date.parse(e.startDate) === targetMs)
  );
  // NOT AN UNCONDITIONAL APPEND, AND THAT IS THE WHOLE POINT — see
  // `seriesExDate`. `null` here is a real answer ("the instant is not one the
  // rule generates, so an exDate would cancel the whole UTC day it sits on"),
  // and the server, handed the same answer, writes nothing either. Appending
  // regardless used to make the two disagree in BOTH directions: an exDate the
  // server skipped was reverted by `applySeriesResult` a moment later and the
  // occurrence came straight back, and an exDate neither of them owed sat in
  // the local rule blanking an unrelated day until the next load().
  if (exDate == null) return dropped;
  return dropped.map((e) =>
    e.id === master.id && e.recurrence
      ? { ...e, recurrence: withExDate(e.recurrence, exDate) }
      : e
  );
}

/** The optimistic picture after a scoped EDIT, before the RPC answers. */
function optimisticSeriesEdit(
  events: readonly CalendarEvent[],
  ctx: SeriesLegContext & { readonly patch: EventUpdate; readonly now: string }
): readonly CalendarEvent[] {
  const { master, target, scope, patch, instants, exDate, now } = ctx;
  const targetMs = Date.parse(target.occurrenceStart);
  const isChildOf = (e: CalendarEvent): boolean =>
    e.parentEventId === master.id;

  if (scope === 'all') {
    const rebuild = touchesSchedule(master, patch);
    const content = contentOnly(patch);
    // The rule the SERVER will store, not the one the patch carries. E-d step 1
    // merges `exDates` and `until` back from the row, so a patch that omits
    // them leaves them standing — while a raw spread dropped them from the
    // LOCAL rule, and Major 1 projects from the local rule: every occurrence
    // the family had already cancelled came back on screen for as long as the
    // RPC was in flight, and stayed back if the returned master never arrived.
    const mergedRule = mergedSeriesRule(master, patch);
    return (
      events
        // 2a: a schedule change invalidates every GENERATED child's instant, so
        // they go and the expander rebuilds them. Authored exceptions
        // (`isOccurrence === false`) survive — that is the entire reason the
        // marker exists.
        .filter((e) => !(rebuild && isChildOf(e) && e.isOccurrence))
        .map((e) => {
          if (e.id === master.id) {
            // The master takes the dates and the rule, and NOT the row keys.
            // The patch is built from whatever row the sheet was showing, which
            // for a click on an authored exception is that EXCEPTION — so a
            // raw spread would stamp the exception's id, author, timestamps,
            // thread and files onto the master.
            return {
              ...e,
              ...withoutRowIdentity(patch),
              ...(mergedRule !== null && { recurrence: mergedRule }),
              updatedAt: now,
            } as CalendarEvent;
          }
          // 2b: content-only edits CORRECT the children in place. Deleting them
          // on a title change would blank a week of reminders and the shared
          // calendar link, both of which read ROWS.
          if (!rebuild && isChildOf(e) && e.isOccurrence) {
            return { ...e, ...content, updatedAt: now } as CalendarEvent;
          }
          return e;
        })
    );
  }

  if (scope === 'this_and_following') {
    const tailId = instants.tailId;
    const tailStart = instants.newStart ?? target.occurrenceStart;
    const kept = events
      .filter((e) => !(isChildOf(e) && Date.parse(e.startDate) >= targetMs))
      .map((e) =>
        e.id === master.id && e.recurrence
          ? {
              ...e,
              recurrence: withUntilBefore(e.recurrence, new Date(targetMs)),
            }
          : e
      );
    if (tailId === undefined) return kept;
    return [
      ...kept,
      tailMasterFor(master, patch, instants, tailId, tailStart, now),
    ];
  }

  // scope `this`
  const destination = instants.newStart ?? target.occurrenceStart;
  // The vacated instant must never be regenerated — WHEN one is owed.
  // `seriesExDate` has already answered all three questions this used to ask
  // inline (is it a move? is it the anchor? is the vacated instant one the rule
  // actually generates?), and it answered them with the value that travels to
  // the server, so the two halves cannot drift. The anchor and a content-only
  // edit both come back `undefined`; an already-moved exception being moved
  // again comes back `null`, because exDating the day it currently occupies
  // would take that day's own genuine occurrence with it.
  const withExDates =
    exDate != null
      ? events.map((e) =>
          e.id === master.id && e.recurrence
            ? { ...e, recurrence: withExDate(e.recurrence, exDate) }
            : e
        )
      : events;

  const existing = withExDates.find(
    (e) =>
      (e.id === target.occurrenceId && target.occurrenceId !== null) ||
      (isChildOf(e) && Date.parse(e.startDate) === targetMs)
  );

  if (target.isMasterOccurrence) {
    if (instants.nextStart === undefined) {
      // E-b's no-further-occurrence arm, and it is NOT a delete on either side.
      // The server patches the master IN PLACE and leaves its rule alone;
      // delegating to `optimisticSeriesDelete` here took that helper's own
      // no-next branch, which drops the master AND every row under it — and
      // `applySeriesResult` only ever puts back the master, the exception and
      // the tail, so every generated child of a rule whose last occurrence was
      // being edited stayed gone until the next `load()`.
      //
      // The rule is untouched for free: the patch for scope `this` has already
      // had `recurrence` stripped by the caller.
      return withExDates.map((e) =>
        e.id === master.id
          ? ({
              ...e,
              ...withoutRowIdentity(patch),
              startDate: destination,
              ...(instants.newEnd !== undefined && {
                endDate: instants.newEnd,
              }),
              updatedAt: now,
            } as CalendarEvent)
          : e
      );
    }
    // The anchor roll first, then the exception is authored at the destination
    // by the RPC — the optimistic picture only rolls the master, and
    // `applySeriesResult` inserts the returned exception row.
    return optimisticSeriesDelete(withExDates, {
      master,
      target,
      scope,
      instants,
      exDate,
    });
  }

  if (existing) {
    return withExDates.map((e) =>
      e.id === existing.id
        ? ({
            ...e,
            // `withoutRowIdentity`, NOT the raw patch. The patch is built from
            // the MASTER and carries `id: <master id>`, so spreading it whole
            // gave the edited occurrence the master's id — two rows in
            // `state.events` sharing one id, which every `find`/`map` keyed on
            // id then resolves to whichever comes first. It carried the
            // master's `createdAt`, `createdBy`, `comments` and `attachments`
            // over the row's own, too. The DATES stay: this row is exactly the
            // one the edit is about.
            ...withoutRowIdentity(patch),
            startDate: destination,
            // The row becomes an AUTHORED EXCEPTION. This is what makes it
            // invisible to the expander's prune, forever.
            isOccurrence: false,
            updatedAt: now,
          } as CalendarEvent)
        : e
    );
  }

  // The target was a PROJECTION — there is no row to patch. The RPC inserts
  // one and `applySeriesResult` folds it in; nothing is minted locally, because
  // a client-invented uuid here would be a second id for the same row.
  return withExDates;
}

/**
 * The optimistic tail master of a `this_and_following` split.
 *
 * Its id is the CLIENT-MINTED `tailId` the RPC inserts with, so the optimistic
 * row and the persisted row are the same row and reconcile without a remap.
 * `until` and `count` are stripped (the tail is unbounded again) and exDates
 * below the split point are dropped — they belong to the head series and would
 * be inert noise here.
 */
function tailMasterFor(
  master: CalendarEvent,
  patch: EventUpdate,
  instants: SeriesInstants,
  tailId: string,
  tailStart: string,
  now: string
): CalendarEvent {
  const source = patch.recurrence ?? master.recurrence;
  const splitMs = Date.parse(tailStart);
  let rule: RecurrenceRule | undefined;
  if (source) {
    const { until: _u, count: _c, exDates: _x, ...rest } = source;
    const kept = (source.exDates ?? []).filter(
      (iso) => Date.parse(iso) >= splitMs
    );
    rule = {
      ...rest,
      ...(kept.length > 0 && { exDates: kept }),
      ...(instants.tailCount !== undefined && { count: instants.tailCount }),
    };
  }
  return {
    ...master,
    // Row keys stripped: the patch may have been built from an authored
    // EXCEPTION (that is the row the sheet shows for a click on one), and the
    // tail is a brand-new master whose id, author and timestamps are decided
    // here and by the RPC — `events_insert_member` forces
    // `created_by = auth.uid()` server-side either way.
    ...withoutRowIdentity(patch),
    id: tailId,
    startDate: tailStart,
    ...(instants.newEnd !== undefined && { endDate: instants.newEnd }),
    ...(rule !== undefined && { recurrence: rule }),
    comments: [],
    attachments: [],
    isOccurrence: false,
    createdAt: now,
    updatedAt: now,
  } as CalendarEvent;
}

/**
 * Fold a row the SERVER answered with over the copy the store already holds,
 * KEEPING THE TWO RELATIONS THE ROW DOES NOT CARRY.
 *
 * `rowToEvent` hardcodes `comments: []` and `attachments: []` — correct for a
 * fresh read, where the relations genuinely were not selected, and correct for
 * `createEvent`, whose row is brand new and whose attachments
 * `hydrateAfterWrite` fills in. It is NOT correct for a row folded over one the
 * store already has: `to_jsonb(e)` inside the series RPCs is the `events` row
 * and nothing else, so an unguarded swap blanks the master's comment thread and
 * its file list on every scoped write, until the next full `load()`.
 *
 * This is the discipline the other write paths already keep, by never facing
 * the question: the plain `updateEvent` DISCARDS the service's return value and
 * keeps its own optimistic row, and `reconcileEvent` runs only on CREATE, where
 * the thread is empty by construction. Here the returned row is genuinely
 * needed — it carries the merged rule and the server's `updated_at` — so the
 * relations are carried over instead.
 */
function foldServerRow(
  existing: CalendarEvent,
  incoming: CalendarEvent
): CalendarEvent {
  return {
    ...incoming,
    comments: existing.comments,
    attachments: existing.attachments,
  };
}

/**
 * Fold an RPC's returned rows back into the store.
 *
 * The RPC answers with whole `events` rows, so no second round trip is needed —
 * and reconciling by id also swaps the CLIENT-MINTED tail id for the persisted
 * row without a remap, because the insert used the id we sent.
 *
 * ****************************************************************************
 * ***  `tail: null` ON A LEG THAT MINTED ONE IS AN ANSWER, NOT A SILENCE.  ***
 * ****************************************************************************
 * `mintedTailId` is `instants.tailId` — the id the optimistic set already
 * inserted a tail master under. E-c ALWAYS answers with the tail it inserted
 * (it re-selects the row and raises when RLS refused it), so a null tail means
 * the split never happened: 029 re-scoped `this_and_following` at or below the
 * master's own start to `all` and edited the whole series instead. Nothing then
 * puts the optimistic tail back — `removedIds` lists rows the RPC DELETED, and
 * that row was never on the server to delete — so it stood in `state.events` as
 * a phantom second series until the next `load()`, complete with its own pills
 * and its own projections.
 *
 * This is the success path catching up with the failure path: the rollback has
 * always filtered the minted tail out by id, for exactly the same reason.
 */
function applySeriesResult(
  result: SeriesWriteResult,
  mintedTailId?: string
): void {
  useCalendarStore.setState((state) => {
    const removed = new Set(result.removedIds);
    if (mintedTailId !== undefined && result.tail?.id !== mintedTailId) {
      removed.add(mintedTailId);
    }
    const upserts = [result.master, result.exception, result.tail].filter(
      (e): e is CalendarEvent => e != null
    );
    const byId = new Map(upserts.map((e) => [e.id, e] as const));
    const kept = state.events
      .filter((e) => !removed.has(e.id))
      .map((e) => {
        const incoming = byId.get(e.id);
        return incoming ? foldServerRow(e, incoming) : e;
      });
    const present = new Set(kept.map((e) => e.id));
    const added = upserts.filter((e) => !present.has(e.id));
    return {
      events: added.length > 0 ? [...kept, ...added] : kept,
      selectedEventId:
        state.selectedEventId !== null && removed.has(state.selectedEventId)
          ? null
          : state.selectedEventId,
    };
  });
}

// ============================================
// Store Implementation
// ============================================

/**
 * Calendar store with Zustand
 *
 * @example
 * ```tsx
 * const calendars = useCalendarStore((state) => state.calendars);
 * const addEvent = useCalendarStore((state) => state.addEvent);
 *
 * // Hydrate from Supabase on mount
 * useEffect(() => {
 *   void useCalendarStore.getState().load();
 * }, []);
 * ```
 */
export const useCalendarStore = create<CalendarState>((set, get) => ({
  // Initial state
  ...initialState,

  // ============================================
  // Calendar CRUD
  // ============================================

  setCalendars: (calendars) => set({ calendars }),

  addCalendar: (calendar) => {
    set((state) => ({
      calendars: [...state.calendars, calendar],
    }));

    persistCalendarMutation(
      'Календарът не можа да бъде запазен.',
      async () => {
        const persisted = await calendarService.createCalendar({
          name: calendar.name,
          isPublic: calendar.isPublic,
          weekStartsOn: calendar.weekStartsOn,
          ...(calendar.description !== undefined && {
            description: calendar.description,
          }),
          ...(calendar.coverImage !== undefined && {
            coverImage: calendar.coverImage,
          }),
        });
        reconcileCalendar(calendar.id, persisted);
      },
      () => {
        useCalendarStore.setState((state) => ({
          calendars: state.calendars.filter((cal) => cal.id !== calendar.id),
          selectedCalendarId:
            state.selectedCalendarId === calendar.id
              ? null
              : state.selectedCalendarId,
          visibleCalendarIds: state.visibleCalendarIds.filter(
            (cid) => cid !== calendar.id
          ),
        }));
      }
    );
  },

  updateCalendar: (id, updates) => {
    const previousCalendar = get().calendars.find((cal) => cal.id === id);

    set((state) => ({
      calendars: state.calendars.map((cal) =>
        cal.id === id
          ? {
              ...cal,
              ...updates,
              updatedAt: new Date().toISOString(),
            }
          : cal
      ),
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await calendarService.updateCalendar(id, {
            ...(updates.name !== undefined && { name: updates.name }),
            ...(updates.description !== undefined && {
              description: updates.description,
            }),
            ...(updates.coverImage !== undefined && {
              coverImage: updates.coverImage,
            }),
            ...(updates.isPublic !== undefined && {
              isPublic: updates.isPublic,
            }),
            ...(updates.weekStartsOn !== undefined && {
              weekStartsOn: updates.weekStartsOn,
            }),
          });
        },
        () => {
          if (!previousCalendar) return;
          useCalendarStore.setState((state) => ({
            calendars: state.calendars.map((cal) =>
              cal.id === id ? previousCalendar : cal
            ),
          }));
        }
      );
    }
  },

  deleteCalendar: (id) => {
    const deletedCalendar = get().calendars.find((cal) => cal.id === id);
    const deletedEvents = get().events.filter((evt) => evt.calendarId === id);
    const wasSelected = get().selectedCalendarId === id;

    set((state) => ({
      calendars: state.calendars.filter((cal) => cal.id !== id),
      // Also delete all events belonging to this calendar
      events: state.events.filter((evt) => evt.calendarId !== id),
      // Clear selection if deleted calendar was selected
      selectedCalendarId:
        state.selectedCalendarId === id ? null : state.selectedCalendarId,
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await calendarService.deleteCalendar(id);
        },
        () => {
          if (!deletedCalendar) return;
          useCalendarStore.setState((state) => ({
            calendars: state.calendars.some((cal) => cal.id === id)
              ? state.calendars
              : [...state.calendars, deletedCalendar],
            events: [
              ...state.events,
              ...deletedEvents.filter(
                (de) => !state.events.some((evt) => evt.id === de.id)
              ),
            ],
            selectedCalendarId:
              wasSelected && state.selectedCalendarId === null
                ? id
                : state.selectedCalendarId,
          }));
        }
      );
    }
  },

  selectCalendar: (id) => set({ selectedCalendarId: id }),

  // ============================================
  // Event CRUD
  // ============================================

  setEvents: (events) => set({ events }),

  addEvent: (event) => {
    // Last line of defence for the `calendar_id` uuid contract.
    //
    // `events.calendar_id` is `uuid NOT NULL`. Anything that is not a uuid —
    // a mock-era sentinel, an unreconciled optimistic calendar id — is a
    // guaranteed `22P02` at the database, and offline it is worse than that:
    // the mutation queues successfully, the user is told the event was
    // created, and it only dies on replay in the dead-letter queue where
    // nobody looks. Refusing here keeps the optimistic event out of the store
    // entirely, so the calendar never shows a row that can never persist.
    //
    // Callers are expected to have resolved a real calendar first (see
    // `selectWritableCalendarId`); reaching this branch is a bug, so it is
    // reported rather than silently swallowed.
    if (!isPersistedId(event.calendarId)) {
      const message =
        'Събитието не може да бъде запазено: няма избран календар.';
      set({ error: message });
      toast({
        variant: ToastVariant.Destructive,
        title: 'Грешка при запазване',
        description: message,
      });
      return;
    }

    set((state) => ({
      events: [...state.events, event],
    }));

    persistCalendarMutation(
      'Събитието не можа да бъде запазено.',
      async () => {
        const persisted = await calendarService.createEvent(
          eventToCreateDTO(event)
        );
        reconcileEvent(event.id, persisted);
      },
      () => {
        useCalendarStore.setState((state) => ({
          events: state.events.filter((evt) => evt.id !== event.id),
          selectedEventId:
            state.selectedEventId === event.id ? null : state.selectedEventId,
        }));
      }
    );
  },

  updateEvent: (id, updates, changedBy) => {
    const previousEvent = get().events.find((evt) => evt.id === id);
    const previousLog = get().eventChangeLog[id];

    // Defensive refusal — see refuseSeriesOnPlainPath. A series row edited
    // through this path would write a single-row PATCH with no exDate, no rule
    // change and no transaction, and offline it would mint a queue entry the
    // expander undoes on its next run.
    if (previousEvent && isSeriesRow(previousEvent)) {
      refuseSeriesOnPlainPath();
      return;
    }

    const now = new Date().toISOString();
    // Merge updates over prev. An explicit `undefined` in `updates` clears that
    // optional field (so it yields a "removed" diff), whereas an omitted key
    // inherits the previous value.
    const next: CalendarEvent | null = previousEvent
      ? ({ ...previousEvent, ...updates, updatedAt: now } as CalendarEvent)
      : null;
    const records =
      previousEvent && next
        ? diffEventFields(previousEvent, next, changedBy, now)
        : [];

    set((state) => {
      if (!next || !state.events.some((evt) => evt.id === id)) {
        return {};
      }

      const eventChangeLog =
        records.length > 0
          ? {
              ...state.eventChangeLog,
              [id]: [...(state.eventChangeLog[id] ?? []), ...records],
            }
          : state.eventChangeLog;

      return {
        events: state.events.map((evt) => (evt.id === id ? next : evt)),
        eventChangeLog,
      };
    });

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await calendarService.updateEvent(id, eventUpdatesToDTO(updates));
          // AFTER the PATCH has returned, never concurrently with it.
          // `event_changes` has no DELETE policy, so a row describing a change
          // that never landed could never be cleaned up.
          //
          // AND IT CANNOT FAIL THIS CLOSURE. See persistChangeRecordsSoft: a
          // refused history row used to reject here, which fired the rollback
          // below and reverted a PATCH the server had already committed —
          // while showing raw English PostgREST prose in a Bulgarian toast.
          await persistChangeRecordsSoft(id, records, previousLog);
        },
        () => {
          if (!previousEvent) return;
          useCalendarStore.setState((state) => ({
            events: state.events.map((evt) =>
              evt.id === id ? previousEvent : evt
            ),
          }));
          restoreChangeLog(id, previousLog);
        }
      );
    }
  },

  deleteEvent: (id) => {
    const deletedEvent = get().events.find((evt) => evt.id === id);

    // Defensive refusal — see refuseSeriesOnPlainPath. Deleting a series row
    // here removes it without writing an exDate, so the next expander run
    // re-materializes it at the same instant and the family sees an event they
    // deleted come back with nothing in the UI able to explain why.
    if (deletedEvent && isSeriesRow(deletedEvent)) {
      refuseSeriesOnPlainPath();
      return;
    }

    set((state) => ({
      events: state.events.filter((evt) => evt.id !== id),
      // Clear selection if deleted event was selected
      selectedEventId:
        state.selectedEventId === id ? null : state.selectedEventId,
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await calendarService.deleteEvent(id);
        },
        () => {
          if (!deletedEvent) return;
          useCalendarStore.setState((state) =>
            state.events.some((evt) => evt.id === id)
              ? {}
              : { events: [...state.events, deletedEvent] }
          );
        }
      );
    }
  },

  // ============================================
  // Series writes (Wave 8, Major 2)
  // ============================================

  updateEventSeries: (target, scope, updates, changedBy) => {
    const master = get().events.find((evt) => evt.id === target.masterId);
    if (!master) {
      refuseSeriesWrite();
      return;
    }

    // Rebasing FIRST, because everything below depends on it. The form was
    // filled from the OCCURRENCE the user clicked, so an unrebased `all` edit
    // would move the master to the day they happened to be looking at.
    const rebased = rebaseUpdatesForScope(updates, target, master, scope);
    // The patch for scope `this` NEVER carries the rule: it is a master-only
    // field and an exception carries none. If the user changed the rule and
    // then picked „Само това събитие" the rule change is DROPPED, and the
    // chooser's copy says so.
    const { recurrence: _ruleDropped, ...withoutRule } = rebased;
    const scoped: EventUpdate = scope === 'this' ? withoutRule : rebased;
    // ************************************************************************
    // ***  RISK 24, THROUGH THE DOOR THE `<=` DELEGATION OPENED.           ***
    // ************************************************************************
    // `ruleForScopedEdit` drops `exDates`/`until` for scope `all` because the
    // rule the modal merges is read from a `state.events` snapshot that is
    // stale by the realtime lag, and 029's E-d step 1 preserves those two keys
    // only when the patch OMITS them: present-but-stale is indistinguishable
    // from deliberate, so the write wins and another member's „отмени вторник"
    // — written thirty seconds earlier — is silently gone.
    //
    // It exempts `this_and_following`, correctly, because that leg's rule is
    // the CONSTRUCTOR for the tail master rather than a patch of the head's.
    // But 029 re-scopes `this_and_following` at or below the master's own start
    // to `all`, and the delegated leg runs E-d step 1 with the patch it was
    // handed — reopening the clobber on a leg nothing was stripping. The scope
    // the modal held is the one the USER picked; `delegatesToAll` is the one
    // the SERVER will run, and it is decidable here.
    const patch: EventUpdate =
      scoped.recurrence != null && delegatesToAll(target, master, scope)
        ? {
            ...scoped,
            recurrence: withoutServerPreservedKeys(scoped.recurrence),
          }
        : scoped;
    const instants = seriesInstants(target, master, scope, patch);
    // See deleteEventSeries: the optimistic append must be the value that
    // travels, not a second opinion about it.
    const exDate = instants.exDate;

    const now = new Date().toISOString();
    const snapshot = seriesRowsOf(get().events, target.masterId);

    // ---- change records ---------------------------------------------------
    // ONE batch, on the row the user authored: the master for `all` and
    // `this_and_following`, the target row for `this`. The RPC's server-side
    // child writes produce none — children are not user-authored rows — and a
    // scope-`this` edit that CREATES an exception produces none either, because
    // that is a creation, not an edit.
    const recordTargetId =
      scope === 'this' ? target.occurrenceId : target.masterId;
    const recordBase =
      scope === 'this'
        ? get().events.find((evt) => evt.id === target.occurrenceId)
        : master;
    const previousLog = recordTargetId
      ? get().eventChangeLog[recordTargetId]
      : undefined;
    const records =
      recordTargetId && recordBase
        ? diffEventFields(
            recordBase,
            { ...recordBase, ...patch, updatedAt: now } as CalendarEvent,
            changedBy,
            now
          )
        : [];

    // ---- optimistic set ---------------------------------------------------
    set((state) => ({
      events: optimisticSeriesEdit(state.events, {
        master,
        target,
        scope,
        patch,
        instants,
        exDate,
        now,
      }),
      ...(records.length > 0 &&
        recordTargetId && {
          eventChangeLog: {
            ...state.eventChangeLog,
            [recordTargetId]: [
              ...(state.eventChangeLog[recordTargetId] ?? []),
              ...records,
            ],
          },
        }),
    }));

    persistCalendarMutation(
      'Промяната на поредицата не можа да бъде запазена.',
      async () => {
        const result = await calendarService.updateEventSeries(
          target,
          scope,
          eventUpdatesToDTO(patch),
          instants
        );
        // `instants.tailId` is passed on EVERY edit leg, not only the split:
        // it is `undefined` unless a tail was actually minted, and passing it
        // unconditionally is what makes the reconciliation impossible to
        // forget the next time a leg learns to mint one.
        applySeriesResult(result, instants.tailId);
        if (recordTargetId) {
          // For scope `this` against a row that existed, `recordTargetId` is
          // that row's id and it survives the leg (it becomes an authored
          // exception rather than being replaced), so the history stays
          // attached to the row the user edited.
          //
          // SOFT, and on this path that is not a nicety. The RPC has COMMITTED
          // by the time this runs — children deleted, master moved, a tail
          // master inserted — and the rollback below re-appends every one of
          // those rows. A refused audit row must not put a series back on
          // screen that the database no longer holds.
          await persistChangeRecordsSoft(recordTargetId, records, previousLog);
        }
      },
      () => {
        restoreEventsById(snapshot);
        // The tail master of a split is the one row this leg ADDS, so it is not
        // in the snapshot and restoring by id alone would leave it standing —
        // a second, phantom series on screen for a write that was refused.
        if (instants.tailId !== undefined) {
          const tailId = instants.tailId;
          useCalendarStore.setState((state) => ({
            events: state.events.filter((evt) => evt.id !== tailId),
          }));
        }
        if (!recordTargetId) return;
        restoreChangeLog(recordTargetId, previousLog);
      }
    );
  },

  deleteEventSeries: (target, scope) => {
    const master = get().events.find((evt) => evt.id === target.masterId);
    if (!master) {
      refuseSeriesWrite();
      return;
    }

    const instants = seriesInstants(target, master, scope);
    // READ OFF `instants`, never recomputed: the optimistic append and the value
    // the RPC receives have to be the same string, or `applySeriesResult` folds
    // the returned master back over a local rule that disagrees with it and the
    // cancelled occurrence reappears the moment the write succeeds.
    const exDate = instants.exDate;
    const snapshot = seriesRowsOf(get().events, target.masterId);

    set((state) => ({
      events: optimisticSeriesDelete(state.events, {
        master,
        target,
        scope,
        instants,
        exDate,
      }),
      selectedEventId:
        state.selectedEventId !== null &&
        snapshot.some((e) => e.id === state.selectedEventId)
          ? null
          : state.selectedEventId,
    }));

    persistCalendarMutation(
      'Изтриването на поредицата не можа да бъде запазено.',
      async () => {
        const result = await calendarService.deleteEventSeries(
          target,
          scope,
          instants
        );
        applySeriesResult(result);
      },
      () => {
        restoreEventsById(snapshot);
      }
    );
  },

  upsertRealtimeEvent: (event) =>
    set((state) => {
      const existing = state.events.find((evt) => evt.id === event.id);
      if (existing) {
        // Drop stale echoes (e.g. our own optimistic write coming back).
        if (existing.updatedAt >= event.updatedAt) {
          return {};
        }
        // `foldServerRow` for the same reason `applySeriesResult` uses it: the
        // payload is `rowToEvent(row)` over a realtime `events` row, which
        // carries no `event_attachments` and no `event_comments`, so a straight
        // swap blanked the thread and the file list of any event another device
        // touched.
        return {
          events: state.events.map((evt) =>
            evt.id === event.id ? foldServerRow(evt, event) : evt
          ),
        };
      }
      return { events: [...state.events, event] };
    }),

  deleteRealtimeEvent: (id) =>
    set((state) => ({
      events: state.events.filter((evt) => evt.id !== id),
      // Clear selection if the deleted event was selected.
      selectedEventId:
        state.selectedEventId === id ? null : state.selectedEventId,
    })),

  toggleEventPin: (id) => {
    const now = new Date().toISOString();
    const wasPinned = get().events.find((evt) => evt.id === id)?.isPinned;

    set((state) => ({
      events: state.events.map((evt) =>
        evt.id === id
          ? {
              ...evt,
              isPinned: !evt.isPinned,
              updatedAt: now,
            }
          : evt
      ),
    }));

    if (isPersistedId(id) && wasPinned !== undefined) {
      persistCalendarMutation(
        'Закачането не можа да бъде запазено.',
        async () => {
          await calendarService.updateEvent(id, { isPinned: !wasPinned });
        },
        () => {
          useCalendarStore.setState((state) => ({
            events: state.events.map((evt) =>
              evt.id === id ? { ...evt, isPinned: wasPinned } : evt
            ),
          }));
        }
      );
    }
  },

  selectEvent: (id) => set({ selectedEventId: id }),

  // ============================================
  // Label CRUD
  // ============================================

  addLabel: (label) => {
    set((state) => ({
      labels: [...state.labels, label],
    }));

    if (isPersistedId(label.calendarId)) {
      persistCalendarMutation(
        'Етикетът не можа да бъде запазен.',
        async () => {
          const persisted = await calendarService.createLabel(
            label.calendarId,
            {
              name: label.name,
              color: label.color,
              ...(label.emoji !== undefined && { emoji: label.emoji }),
            }
          );
          reconcileLabel(label.id, persisted);
        },
        () => {
          useCalendarStore.setState((state) => ({
            labels: state.labels.filter((lbl) => lbl.id !== label.id),
          }));
        }
      );
    }
  },

  updateLabel: (id, updates) => {
    const previousLabel = get().labels.find((lbl) => lbl.id === id);

    set((state) => ({
      labels: state.labels.map((lbl) =>
        lbl.id === id ? { ...lbl, ...updates } : lbl
      ),
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await calendarService.updateLabel(id, {
            ...(updates.name !== undefined && { name: updates.name }),
            ...(updates.color !== undefined && { color: updates.color }),
            // An emoji of `''` is a real edit — "clear it" — and has to travel
            // as a value, not as an omission. `calendar_labels.emoji` is
            // nullable and the service turns `''` into NULL; omitting the key
            // here would mean "not edited" and the clear would never persist.
            ...(updates.emoji !== undefined && { emoji: updates.emoji }),
            ...(updates.sortOrder !== undefined && {
              sortOrder: updates.sortOrder,
            }),
          });
        },
        () => {
          if (!previousLabel) return;
          useCalendarStore.setState((state) => ({
            labels: state.labels.map((lbl) =>
              lbl.id === id ? previousLabel : lbl
            ),
          }));
        }
      );
    }
  },

  deleteLabel: (id) => {
    const deletedLabel = get().labels.find((lbl) => lbl.id === id);
    const affectedEventIds = get()
      .events.filter((evt) => evt.labelId === id)
      .map((evt) => evt.id);

    set((state) => ({
      labels: state.labels.filter((lbl) => lbl.id !== id),
      // Remove label reference from events - use omit pattern for exactOptionalPropertyTypes
      events: state.events.map((evt) => {
        if (evt.labelId === id) {
          // Destructure to remove labelId, then spread rest
          const { labelId: _, ...rest } = evt;
          return rest;
        }
        return evt;
      }),
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await calendarService.deleteLabel(id);
        },
        () => {
          if (!deletedLabel) return;
          useCalendarStore.setState((state) => ({
            labels: state.labels.some((lbl) => lbl.id === id)
              ? state.labels
              : [...state.labels, deletedLabel],
            events: state.events.map((evt) =>
              affectedEventIds.includes(evt.id) && evt.labelId === undefined
                ? { ...evt, labelId: id }
                : evt
            ),
          }));
        }
      );
    }
  },

  // ============================================
  // Calendar Members
  // ============================================

  addCalendarMember: (calendarMember) => {
    set((state) => ({
      calendarMembers: [...state.calendarMembers, calendarMember],
    }));

    if (
      isPersistedId(calendarMember.calendarId) &&
      isPersistedId(calendarMember.memberId)
    ) {
      persistCalendarMutation(
        'Членът не можа да бъде добавен.',
        async () => {
          await calendarService.addMember(calendarMember.calendarId, {
            memberId: calendarMember.memberId,
            role: calendarMember.role,
          });
        },
        () => {
          useCalendarStore.setState((state) => ({
            calendarMembers: state.calendarMembers.filter(
              (cm) =>
                !(
                  cm.calendarId === calendarMember.calendarId &&
                  cm.memberId === calendarMember.memberId
                )
            ),
          }));
        }
      );
    }
  },

  updateCalendarMember: (calendarId, memberId, updates) => {
    const previousMember = get().calendarMembers.find(
      (cm) => cm.calendarId === calendarId && cm.memberId === memberId
    );

    set((state) => ({
      calendarMembers: state.calendarMembers.map((cm) =>
        cm.calendarId === calendarId && cm.memberId === memberId
          ? { ...cm, ...updates }
          : cm
      ),
    }));

    if (isPersistedId(calendarId) && isPersistedId(memberId)) {
      persistCalendarMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          if (updates.role !== undefined) {
            await calendarService.updateMemberRole(
              calendarId,
              memberId,
              updates.role
            );
          }
          if ('displayNameOverride' in updates || 'avatarOverride' in updates) {
            await calendarService.updateMemberOverrides(calendarId, memberId, {
              ...('displayNameOverride' in updates && {
                displayNameOverride: updates.displayNameOverride ?? null,
              }),
              ...('avatarOverride' in updates && {
                avatarOverride: updates.avatarOverride ?? null,
              }),
            });
          }
        },
        () => {
          if (!previousMember) return;
          useCalendarStore.setState((state) => ({
            calendarMembers: state.calendarMembers.map((cm) =>
              cm.calendarId === calendarId && cm.memberId === memberId
                ? previousMember
                : cm
            ),
          }));
        }
      );
    }
  },

  removeCalendarMember: (calendarId, memberId) => {
    const removedMember = get().calendarMembers.find(
      (cm) => cm.calendarId === calendarId && cm.memberId === memberId
    );

    set((state) => ({
      calendarMembers: state.calendarMembers.filter(
        (cm) => !(cm.calendarId === calendarId && cm.memberId === memberId)
      ),
    }));

    if (isPersistedId(calendarId) && isPersistedId(memberId)) {
      persistCalendarMutation(
        'Премахването не можа да бъде запазено.',
        async () => {
          await calendarService.removeMember(calendarId, memberId);
        },
        () => {
          if (!removedMember) return;
          useCalendarStore.setState((state) => ({
            calendarMembers: state.calendarMembers.some(
              (cm) => cm.calendarId === calendarId && cm.memberId === memberId
            )
              ? state.calendarMembers
              : [...state.calendarMembers, removedMember],
          }));
        }
      );
    }
  },

  // ============================================
  // Event Comments
  // ============================================

  addComment: (eventId, text, authorId) => {
    const now = new Date().toISOString();
    const newComment: EventComment = {
      id: `comment-${Date.now()}`,
      eventId,
      text,
      authorId,
      createdAt: now,
    };

    set((state) => ({
      events: state.events.map((evt) =>
        evt.id === eventId
          ? {
              ...evt,
              comments: [...evt.comments, newComment],
              updatedAt: now,
            }
          : evt
      ),
    }));

    if (isPersistedId(eventId)) {
      persistCalendarMutation(
        'Коментарът не можа да бъде запазен.',
        async () => {
          const persisted = await calendarService.addEventComment(
            eventId,
            text,
            authorId
          );
          reconcileComment(newComment.id, persisted);
        },
        () => {
          useCalendarStore.setState((state) => ({
            events: state.events.map((evt) =>
              evt.id === eventId
                ? {
                    ...evt,
                    comments: evt.comments.filter(
                      (c) => c.id !== newComment.id
                    ),
                  }
                : evt
            ),
          }));
        }
      );
    }

    return newComment;
  },

  updateComment: (eventId, commentId, text) => {
    const previousComment = get()
      .events.find((evt) => evt.id === eventId)
      ?.comments.find((c) => c.id === commentId);
    const now = new Date().toISOString();

    set((state) => ({
      events: state.events.map((evt) =>
        evt.id === eventId
          ? {
              ...evt,
              comments: evt.comments.map((c) =>
                c.id === commentId ? { ...c, text, updatedAt: now } : c
              ),
              updatedAt: now,
            }
          : evt
      ),
    }));

    if (isPersistedId(commentId)) {
      persistCalendarMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await calendarService.updateEventComment(commentId, text);
        },
        () => {
          if (!previousComment) return;
          useCalendarStore.setState((state) => ({
            events: state.events.map((evt) =>
              evt.id === eventId
                ? {
                    ...evt,
                    comments: evt.comments.map((c) =>
                      c.id === commentId ? previousComment : c
                    ),
                  }
                : evt
            ),
          }));
        }
      );
    }
  },

  deleteComment: (eventId, commentId) => {
    const deletedComment = get()
      .events.find((evt) => evt.id === eventId)
      ?.comments.find((c) => c.id === commentId);
    const now = new Date().toISOString();

    set((state) => ({
      events: state.events.map((evt) =>
        evt.id === eventId
          ? {
              ...evt,
              comments: evt.comments.filter((c) => c.id !== commentId),
              updatedAt: now,
            }
          : evt
      ),
    }));

    if (isPersistedId(commentId)) {
      persistCalendarMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await calendarService.deleteEventComment(commentId);
        },
        () => {
          if (!deletedComment) return;
          useCalendarStore.setState((state) => ({
            events: state.events.map((evt) =>
              evt.id === eventId &&
              !evt.comments.some((c) => c.id === commentId)
                ? { ...evt, comments: [...evt.comments, deletedComment] }
                : evt
            ),
          }));
        }
      );
    }
  },

  // ============================================
  // Share Links
  // ============================================

  generateShareLink: async (
    calendarId,
    permission,
    expiration,
    createdById,
    createdByName
  ) => {
    const now = new Date();

    // The service creates the DB-backed token so the public
    // `/shared/:token` URL is valid from the very first copy.
    const link = await calendarService.createShareLink(
      calendarId,
      permission,
      expiration,
      createdById,
      createdByName
    );
    set((state) => ({
      calendars: state.calendars.map((cal) =>
        cal.id === calendarId
          ? {
              ...cal,
              shareLinks: [...cal.shareLinks, link],
              updatedAt: now.toISOString(),
            }
          : cal
      ),
    }));
    return link;
  },

  revokeShareLink: (calendarId, linkId) => {
    const now = new Date().toISOString();

    set((state) => ({
      calendars: state.calendars.map((cal) =>
        cal.id === calendarId
          ? {
              ...cal,
              shareLinks: cal.shareLinks.map((link) =>
                link.id === linkId ? { ...link, isActive: false } : link
              ),
              updatedAt: now,
            }
          : cal
      ),
    }));

    if (isPersistedId(linkId)) {
      persistCalendarMutation(
        'Оттеглянето не можа да бъде запазено.',
        async () => {
          await calendarService.revokeShareLink(linkId);
        },
        () => {
          useCalendarStore.setState((state) => ({
            calendars: state.calendars.map((cal) =>
              cal.id === calendarId
                ? {
                    ...cal,
                    shareLinks: cal.shareLinks.map((link) =>
                      link.id === linkId ? { ...link, isActive: true } : link
                    ),
                  }
                : cal
            ),
          }));
        }
      );
    }
  },

  getShareLinksForCalendar: (calendarId) => {
    const { calendars } = get();
    const calendar = calendars.find((c) => c.id === calendarId);
    return calendar?.shareLinks ?? [];
  },

  // ============================================
  // Memo Actions
  // ============================================

  addMemo: (data) => {
    const now = new Date().toISOString();
    const newMemo: Memo = {
      id: `memo-${Date.now()}`,
      calendarId: data.calendarId,
      authorId: data.authorId,
      title: data.title,
      content: data.content ?? '',
      color: data.color ?? 'yellow',
      isPinned: data.isPinned ?? false,
      createdAt: now,
      updatedAt: now,
    };

    set((state) => ({
      memos: [...state.memos, newMemo],
    }));

    persistCalendarMutation(
      'Бележката не можа да бъде запазена.',
      async () => {
        const persisted = await calendarService.createMemo(data);
        reconcileMemo(newMemo.id, persisted);
      },
      () => {
        useCalendarStore.setState((state) => ({
          memos: state.memos.filter((memo) => memo.id !== newMemo.id),
        }));
      }
    );

    return newMemo;
  },

  updateMemo: (id, updates) => {
    const previousMemo = get().memos.find((memo) => memo.id === id);
    const now = new Date().toISOString();

    set((state) => ({
      memos: state.memos.map((memo) =>
        memo.id === id
          ? {
              ...memo,
              ...updates,
              updatedAt: now,
            }
          : memo
      ),
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await calendarService.updateMemo(id, updates);
        },
        () => {
          if (!previousMemo) return;
          useCalendarStore.setState((state) => ({
            memos: state.memos.map((memo) =>
              memo.id === id ? previousMemo : memo
            ),
          }));
        }
      );
    }
  },

  deleteMemo: (id) => {
    const deletedMemo = get().memos.find((memo) => memo.id === id);

    set((state) => ({
      memos: state.memos.filter((memo) => memo.id !== id),
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await calendarService.deleteMemo(id);
        },
        () => {
          if (!deletedMemo) return;
          useCalendarStore.setState((state) =>
            state.memos.some((memo) => memo.id === id)
              ? {}
              : { memos: [...state.memos, deletedMemo] }
          );
        }
      );
    }
  },

  toggleMemoPin: (id) => {
    const now = new Date().toISOString();
    const wasPinned = get().memos.find((memo) => memo.id === id)?.isPinned;

    set((state) => ({
      memos: state.memos.map((memo) =>
        memo.id === id
          ? {
              ...memo,
              isPinned: !memo.isPinned,
              updatedAt: now,
            }
          : memo
      ),
    }));

    if (isPersistedId(id) && wasPinned !== undefined) {
      persistCalendarMutation(
        'Закачането не можа да бъде запазено.',
        async () => {
          await calendarService.updateMemo(id, { isPinned: !wasPinned });
        },
        () => {
          useCalendarStore.setState((state) => ({
            memos: state.memos.map((memo) =>
              memo.id === id ? { ...memo, isPinned: wasPinned } : memo
            ),
          }));
        }
      );
    }
  },

  // ============================================
  // Anniversary Actions
  // ============================================

  addAnniversary: (data) => {
    const now = new Date().toISOString();
    const newAnniversary: Anniversary = {
      id: `anniversary-${Date.now()}`,
      name: data.name,
      date: data.date,
      type: data.type,
      category: data.category,
      ...(data.description !== undefined && { description: data.description }),
      emoji: data.emoji ?? '🎉',
      color: data.color ?? 'blue',
      isRecurring: data.isRecurring ?? true,
      notifyOnDay: data.notifyOnDay ?? true,
      calendarId: data.calendarId,
      createdBy: data.createdBy,
      createdAt: now,
      updatedAt: now,
    };

    set((state) => ({
      anniversaries: [...state.anniversaries, newAnniversary],
    }));

    persistCalendarMutation(
      'Годишнината не можа да бъде запазена.',
      async () => {
        const persisted = await calendarService.createAnniversary(data);
        reconcileAnniversary(newAnniversary.id, persisted);
      },
      () => {
        useCalendarStore.setState((state) => ({
          anniversaries: state.anniversaries.filter(
            (anniversary) => anniversary.id !== newAnniversary.id
          ),
        }));
      }
    );

    return newAnniversary;
  },

  updateAnniversary: (id, updates) => {
    const previousAnniversary = get().anniversaries.find(
      (anniversary) => anniversary.id === id
    );
    const now = new Date().toISOString();

    set((state) => ({
      anniversaries: state.anniversaries.map((anniversary) =>
        anniversary.id === id
          ? {
              ...anniversary,
              ...updates,
              updatedAt: now,
            }
          : anniversary
      ),
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await calendarService.updateAnniversary(id, updates);
        },
        () => {
          if (!previousAnniversary) return;
          useCalendarStore.setState((state) => ({
            anniversaries: state.anniversaries.map((anniversary) =>
              anniversary.id === id ? previousAnniversary : anniversary
            ),
          }));
        }
      );
    }
  },

  deleteAnniversary: (id) => {
    const deletedAnniversary = get().anniversaries.find(
      (anniversary) => anniversary.id === id
    );

    set((state) => ({
      anniversaries: state.anniversaries.filter(
        (anniversary) => anniversary.id !== id
      ),
    }));

    if (isPersistedId(id)) {
      persistCalendarMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await calendarService.deleteAnniversary(id);
        },
        () => {
          if (!deletedAnniversary) return;
          useCalendarStore.setState((state) =>
            state.anniversaries.some((anniversary) => anniversary.id === id)
              ? {}
              : {
                  anniversaries: [...state.anniversaries, deletedAnniversary],
                }
          );
        }
      );
    }
  },

  // ============================================
  // UI Actions
  // ============================================

  setSelectedDate: (date) => set({ selectedDate: date }),

  setViewedDate: (date) => set({ viewedDate: date }),

  setViewMode: (mode) => set({ viewMode: mode }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  // ============================================
  // Calendar Visibility Actions
  // ============================================

  toggleCalendarVisibility: (calendarId) => {
    const { visibleCalendarIds, calendars } = get();
    const isCurrentlyVisible = visibleCalendarIds.includes(calendarId);

    if (isCurrentlyVisible) {
      // Don't allow hiding all calendars - keep at least one visible
      if (visibleCalendarIds.length <= 1) return;
      set({
        visibleCalendarIds: visibleCalendarIds.filter(
          (id) => id !== calendarId
        ),
        showAllCalendars: false,
      });
    } else {
      const newVisibleIds = [...visibleCalendarIds, calendarId];
      set({
        visibleCalendarIds: newVisibleIds,
        showAllCalendars: newVisibleIds.length === calendars.length,
      });
    }
  },

  setShowAllCalendars: (show) => {
    const { calendars } = get();
    if (show) {
      set({
        visibleCalendarIds: calendars.map((c) => c.id),
        showAllCalendars: true,
      });
    } else {
      set({ showAllCalendars: false });
    }
  },

  showOnlyCalendar: (calendarId) => {
    set({
      visibleCalendarIds: [calendarId],
      showAllCalendars: false,
      selectedCalendarId: calendarId,
    });
  },
  showAllCalendarsView: () => {
    const { calendars } = get();
    set({
      visibleCalendarIds: calendars.map((c) => c.id),
      showAllCalendars: true,
    });
  },

  setVisibleCalendars: (calendarIds) => {
    const { calendars } = get();
    // Keep at least one calendar visible
    const next =
      calendarIds.length > 0
        ? [...calendarIds]
        : calendars.slice(0, 1).map((c) => c.id);
    set({
      visibleCalendarIds: next,
      showAllCalendars: next.length === calendars.length,
    });
  },

  getVisibleEvents: () => {
    const { events, visibleCalendarIds } = get();
    return events.filter((event) =>
      visibleCalendarIds.includes(event.calendarId)
    );
  },

  // ============================================
  // Data Initialization
  // ============================================

  load: async () => {
    const { isLoaded, isLoading } = get();
    // Must not key off `calendars.length`: a family with no calendars (or a
    // failed read) would re-arm the guard, and because the page swaps the
    // skeleton for the calendar on `isLoading`, the remount would call load()
    // again — an endless fetch loop.
    if (isLoaded || isLoading) return;

    set({ isLoading: true, error: null });
    try {
      // The change log is the FIFTH entry, not a follow-up read. Until Wave 8
      // neither loader fetched it at all, so after a reload every edited event
      // fell into ActivityFeed's fallback branch — which attributes the edit to
      // `event.createdBy` and therefore named a specific, innocent person as
      // the editor of every edit in the family's history.
      //
      // IT IS THE ONE ENTRY THAT CANNOT REJECT. `Promise.all` rejects on the
      // first rejection, so an unguarded decorative read blanked the whole
      // calendar — and hard-coupled this bundle to migration 029 already being
      // applied. See loadEventChangeLogSoft.
      const [loadedCalendars, events, memos, anniversaries, eventChangeLog] =
        await Promise.all([
          calendarService.getCalendars(),
          calendarService.getAllEvents(),
          calendarService.getMemos(),
          calendarService.getAnniversaries(),
          loadEventChangeLogSoft(),
        ]);

      // Aggregate labels and members across all calendars.
      const labelResults = await Promise.all(
        loadedCalendars.map((c) => calendarService.getLabels(c.id))
      );
      const memberResults = await Promise.all(
        loadedCalendars.map((c) => calendarService.getMembers(c.id))
      );
      const labels = labelResults.flatMap((l) => [...l]);
      const calendarMembers = memberResults.flatMap((m) => [...m]);

      const allCalendarIds = loadedCalendars.map((c) => c.id);

      set({
        calendars: [...loadedCalendars],
        events: [...events],
        memos: [...memos],
        anniversaries: [...anniversaries],
        // REPLACED WHOLESALE, never merged. A merge would preserve records
        // authored against an UNRECONCILED optimistic event — which
        // `isPersistedId`-gating means were never persisted — so История would
        // show history the database does not hold.
        //
        // ...and `null` is not an empty answer, it is NO answer: the read
        // failed, so the replace is skipped and whatever the store already
        // holds stands. See loadEventChangeLogSoft.
        eventChangeLog:
          eventChangeLog === null
            ? get().eventChangeLog
            : { ...eventChangeLog },
        labels,
        members: mapActiveFamilyMembers(),
        calendarMembers,
        selectedCalendarId: pickWritableCalendarId(loadedCalendars),
        visibleCalendarIds: allCalendarIds,
        showAllCalendars: true,
        isLoading: false,
        isLoaded: true,
        error: null,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Грешка при зареждане на календара',
        isLoading: false,
        isLoaded: true,
      });
    }
  },

  refetch: async () => {
    if (refetchInFlight) return;
    refetchInFlight = true;
    try {
      // The change log is the FIFTH entry, not a follow-up read. Until Wave 8
      // neither loader fetched it at all, so after a reload every edited event
      // fell into ActivityFeed's fallback branch — which attributes the edit to
      // `event.createdBy` and therefore named a specific, innocent person as
      // the editor of every edit in the family's history.
      //
      // IT IS THE ONE ENTRY THAT CANNOT REJECT. `Promise.all` rejects on the
      // first rejection, so an unguarded decorative read blanked the whole
      // calendar — and hard-coupled this bundle to migration 029 already being
      // applied. See loadEventChangeLogSoft.
      const [loadedCalendars, events, memos, anniversaries, eventChangeLog] =
        await Promise.all([
          calendarService.getCalendars(),
          calendarService.getAllEvents(),
          calendarService.getMemos(),
          calendarService.getAnniversaries(),
          loadEventChangeLogSoft(),
        ]);

      const labelResults = await Promise.all(
        loadedCalendars.map((c) => calendarService.getLabels(c.id))
      );
      const memberResults = await Promise.all(
        loadedCalendars.map((c) => calendarService.getMembers(c.id))
      );
      const labels = labelResults.flatMap((l) => [...l]);
      const calendarMembers = memberResults.flatMap((m) => [...m]);

      const allCalendarIds = loadedCalendars.map((c) => c.id);

      set((state) => {
        // Preserve the user's selection/visibility where still valid. "Valid"
        // includes being writable: a selection that survived from before a
        // reconcile (an optimistic, non-uuid calendar id) must be replaced,
        // not carried forward, or the next event created lands on a
        // calendar_id Postgres will reject.
        const selectedCalendarId =
          state.selectedCalendarId !== null &&
          isPersistedId(state.selectedCalendarId) &&
          allCalendarIds.includes(state.selectedCalendarId)
            ? state.selectedCalendarId
            : pickWritableCalendarId(loadedCalendars);
        const retainedVisible = state.visibleCalendarIds.filter((id) =>
          allCalendarIds.includes(id)
        );
        const visibleCalendarIds =
          state.showAllCalendars || retainedVisible.length === 0
            ? allCalendarIds
            : retainedVisible;

        return {
          calendars: [...loadedCalendars],
          events: [...events],
          memos: [...memos],
          anniversaries: [...anniversaries],
          // Replaced wholesale — see load(). And NOT replaced at all when the
          // read failed: `refetch` is the loader that runs against a populated
          // История (realtime reconnect, tab focus, a pull-to-refresh), so it
          // is the one where blanking on a transient failure is visible.
          eventChangeLog:
            eventChangeLog === null
              ? state.eventChangeLog
              : { ...eventChangeLog },
          labels,
          members: mapActiveFamilyMembers(),
          calendarMembers,
          selectedCalendarId,
          visibleCalendarIds,
          showAllCalendars: visibleCalendarIds.length === allCalendarIds.length,
          isLoaded: true,
          error: null,
        };
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Грешка при зареждане на календара',
      });
    } finally {
      refetchInFlight = false;
    }
  },

  reset: () => set(initialState),
}));

// Mirror the family roster into the calendar, instead of snapshotting it once.
//
// `state.members` here is a COPY of `familyStore.members`, and it used to be
// written in exactly two places — `load()` and `refetch()` — both of them a
// one-shot `mapActiveFamilyMembers()` read. Nothing subscribed. So removing a
// member or changing a role in Настройки, which mutates `familyStore` directly
// and tells nobody, left the calendar showing the old roster: a removed member
// still listed in `CalendarMembersTab`, still offered as a participant, still
// rendering on their events. It corrected itself only on the next
// `refetchCalendar()` — i.e. when the tab regained focus or the connection came
// back — which reads as "the app fixed itself eventually" rather than as a bug,
// and is why this survived so long.
//
// Subscribing here (calendar → family) keeps the import direction acyclic and
// familyStore the single source of truth, mirroring `familyStore`'s own
// `useAuthStore.subscribe`. The reference check matters: every `familyStore`
// write replaces the array, and an unconditional copy on every unrelated field
// change (`isLoading`, `error`) would re-render every consumer of
// `state.members` for nothing. The seeds in `load()`/`refetch()` stay — a
// subscription only sees CHANGES, and the calendar may well load after the
// roster already has.
useFamilyStore.subscribe((state, prevState) => {
  if (state.members !== prevState.members) {
    useCalendarStore.setState({ members: mapActiveFamilyMembers() });
  }
});

// ============================================
// Helper Functions
// ============================================

/**
 * Family members ARE the calendar people: map the active family's Supabase
 * members (familyStore) to the shared {@link FamilyMember} shape. Ids are
 * profile UUIDs, so `event.createdBy`/`participants` written in live mode
 * line up.
 */
function mapActiveFamilyMembers(): readonly FamilyMember[] {
  return useFamilyStore.getState().members.map((m) => ({
    id: m.profileId,
    displayName: m.displayName,
    familyRole:
      m.role === UserRole.CHILD
        ? ('child' as const)
        : m.role === UserRole.SUPER_ADMIN
          ? ('super_admin' as const)
          : ('parent' as const),
    status: StatusType.Active,
    joinDate: m.joinedAt,
    createdAt: m.joinedAt,
    updatedAt: m.joinedAt,
    ...(m.avatarColor ? { color: m.avatarColor } : {}),
    ...(m.avatarUrl ? { avatar: m.avatarUrl } : {}),
  }));
}

// ============================================
// Selectors
// ============================================

/**
 * Get events for a specific calendar
 */
export const selectEventsForCalendar = (
  state: CalendarState,
  calendarId: string
): readonly CalendarEvent[] => {
  return state.events.filter((evt) => evt.calendarId === calendarId);
};

/**
 * Get events for the currently selected calendar
 */
export const selectCurrentCalendarEvents = (
  state: CalendarState
): readonly CalendarEvent[] => {
  if (!state.selectedCalendarId) return [];
  return selectEventsForCalendar(state, state.selectedCalendarId);
};

/**
 * Get events for a specific date
 */
export const selectEventsForDate = (
  state: CalendarState,
  date: string
): readonly CalendarEvent[] => {
  return state.events.filter((evt) => {
    const eventDate = toLocalDateKey(evt.startDate);
    return eventDate === date;
  });
};

/**
 * Get events for the selected date
 * Returns empty array if no date is selected
 */
export const selectCurrentDateEvents = (
  state: CalendarState
): readonly CalendarEvent[] => {
  if (!state.selectedDate) return [];
  return selectEventsForDate(state, state.selectedDate);
};

/**
 * Get events for a date range
 */
export const selectEventsInRange = (
  state: CalendarState,
  startDate: string,
  endDate: string
): readonly CalendarEvent[] => {
  return state.events.filter((evt) => {
    const eventDate = toLocalDateKey(evt.startDate);
    return eventDate >= startDate && eventDate <= endDate;
  });
};

/**
 * Get the currently selected calendar
 */
export const selectCurrentCalendar = (
  state: CalendarState
): Calendar | undefined => {
  return state.calendars.find((cal) => cal.id === state.selectedCalendarId);
};

/**
 * The calendar id that child rows (events, labels, share links, calendar
 * members) may be written against — or `null` when there is none.
 *
 * Every one of those tables holds `calendar_id uuid NOT NULL`, so a component
 * that needs a calendar to write into needs a *uuid*, not merely a non-null
 * string. This selector is the single place that decides that, and it returns
 * `null` rather than any fallback value: there is deliberately no sentinel to
 * fall back to, because a non-uuid in a uuid column can only ever fail.
 *
 * `null` happens in exactly two situations, both real and both temporary:
 *   1. hydrate has not finished, or it failed (offline, RLS, network);
 *   2. the family genuinely has no calendar row — a family created before
 *      migration 026's AFTER INSERT trigger existed, in a tab that has not
 *      reloaded since.
 *
 * Callers must render a blocked state, not invent an id. Reloading (which
 * re-runs `load()`) is the fix for both cases, and after 026 case 2 cannot
 * recur, because every `families` insert creates the family's calendar in the
 * same transaction.
 */
export const selectWritableCalendarId = (
  state: CalendarState
): string | null => {
  return state.selectedCalendarId !== null &&
    isPersistedId(state.selectedCalendarId)
    ? state.selectedCalendarId
    : null;
};

/**
 * Get the currently selected event
 */
export const selectCurrentEvent = (
  state: CalendarState
): CalendarEvent | undefined => {
  return state.events.find((evt) => evt.id === state.selectedEventId);
};

/**
 * Get labels for a specific calendar
 */
export const selectLabelsForCalendar = (
  state: CalendarState,
  calendarId: string
): readonly EventLabel[] => {
  return state.labels.filter((lbl) => lbl.calendarId === calendarId);
};

/**
 * Get members for a specific calendar
 */
export const selectMembersForCalendar = (
  state: CalendarState,
  calendarId: string
): readonly FamilyMember[] => {
  const calendarMemberIds = state.calendarMembers
    .filter((cm) => cm.calendarId === calendarId)
    .map((cm) => cm.memberId);

  return state.members.filter((m) => calendarMemberIds.includes(m.id));
};

/**
 * Get upcoming events (next 7 days)
 */
export const selectUpcomingEvents = (
  state: CalendarState
): readonly CalendarEvent[] => {
  const todayStr = getTodayDateKey();
  const futureStr = getRelativeDateKey(7);

  return [...selectEventsInRange(state, todayStr, futureStr)].sort((a, b) =>
    a.startDate.localeCompare(b.startDate)
  );
};

/**
 * Get pinned events
 */
export const selectPinnedEvents = (
  state: CalendarState
): readonly CalendarEvent[] => {
  return state.events.filter((evt) => evt.isPinned);
};

/**
 * Get event count by calendar
 */
export const selectEventCountByCalendar = (
  state: CalendarState
): ReadonlyMap<string, number> => {
  const counts = new Map<string, number>();

  for (const calendar of state.calendars) {
    const count = state.events.filter(
      (evt) => evt.calendarId === calendar.id
    ).length;
    counts.set(calendar.id, count);
  }

  return counts;
};

// ============================================
// Memo Selectors
// ============================================

/**
 * Get memos for a specific calendar
 */
export const selectMemosByCalendar = (
  state: CalendarState,
  calendarId: string
): readonly Memo[] => {
  return state.memos.filter((memo) => memo.calendarId === calendarId);
};

/**
 * Get pinned memos (sorted by most recent first)
 */
export const selectPinnedMemos = (state: CalendarState): readonly Memo[] => {
  return [...state.memos]
    .filter((memo) => memo.isPinned)
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
};

/**
 * Get all memos sorted by pinned first, then by most recent
 */
export const selectSortedMemos = (state: CalendarState): readonly Memo[] => {
  return [...state.memos].sort((a, b) => {
    // Pinned memos first
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    // Then by most recent
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
};

// ============================================
// Re-export Types
// ============================================

export type { CalendarState };

// Clear calendar data whenever the active family changes so the next family
// never sees the previous scope's calendars/events/memos.
registerFamilyScopedReset(() => {
  useCalendarStore.getState().reset();
});
